/**
 * Data source for the 6 dedicated diagnostic service landing pages.
 * Each entry becomes a standalone SEO-optimized route under `/{slug}`.
 * Rendered by `src/pages/DiagnosticServiceLanding.tsx`.
 */

export type DiagLocaleContent = {
  title: string;           // <title> — <60 chars
  metaDesc: string;        // meta description — 150-160 chars
  h1: string;
  intro: string;           // hero paragraph
  name: string;            // display name in breadcrumbs / hero badge
  short: string;           // short description
  priceLabel: string;      // e.g. "od 5.000 RSD"
  keywords: string;        // meta keywords
  indications: string[];   // What we examine — bullet list
  symptoms: string[];      // Symptoms/reasons to book
  preparation: string;     // preparation instructions
  duration: string;
  report: string;          // when patient gets the report
  body: string;            // long-form HTML SEO body (H2/H3 structure)
  pricing: Array<[string, string]>;
  faqs: Array<{ q: string; a: string }>;
};

export type DiagnosticService = {
  slug: string;
  anchor: string;          // corresponds to anchor id on /dijagnostika (for legacy)
  image: string;
  fromPrice: string;       // schema.org offer price (number as string, RSD)
  icon: 'Scan' | 'Waves' | 'Zap' | 'HeartPulse' | 'FlaskConical' | 'Stethoscope';
  medicalTestType?: string;
  sr: DiagLocaleContent;
  en: DiagLocaleContent;
  ru: DiagLocaleContent;
};

export const DIAGNOSTIC_SERVICES: DiagnosticService[] = [
  // ────────────────────────── ULTRAZVUK ──────────────────────────
  {
    slug: 'ultrazvuk-novi-beograd',
    anchor: 'ultrazvuk',
    image: '/images/diagnostika-ultrazvuk.jpg',
    fromPrice: '5000',
    icon: 'Scan',
    medicalTestType: 'https://schema.org/MedicalImagingTechnique',
    sr: {
      title: 'Ultrazvuk Novi Beograd — abdomen, štitna, dojke, zglobovi | od 5.000 RSD',
      metaDesc: 'Ultrazvučna dijagnostika na Novom Beogradu — abdomen, mala karlica, štitna žlezda, dojke, meka tkiva, zglobovi, kukovi kod beba. Cena od 5.000 RSD, nalaz odmah.',
      h1: 'Ultrazvuk Novi Beograd — kompletna ultrazvučna dijagnostika',
      name: 'Ultrazvučna dijagnostika',
      short: 'Bezbolan pregled bez zračenja na modernim aparatima visoke rezolucije.',
      intro: 'Ultrazvučni pregled u partnerskoj ordinaciji na Bulevaru umetnosti 15 obavljaju iskusni radiolozi i specijalisti na savremenim aparatima. Bez zračenja, bez bola, sa pisanim nalazom odmah nakon pregleda. Cena od 5.000 RSD.',
      priceLabel: 'od 5.000 RSD',
      keywords: 'ultrazvuk Novi Beograd, ultrazvuk cena, ultrazvuk abdomena, ultrazvuk štitne žlezde, ultrazvuk dojki, ultrazvuk kukova beba, ultrazvuk mekih tkiva',
      indications: [
        'Ultrazvuk abdomena (jetra, žučna kesa, pankreas, slezina, bubrezi)',
        'Ultrazvuk male karlice (materica, jajnici, mokraćna bešika, prostata)',
        'Ultrazvuk štitne žlezde i vrata',
        'Ultrazvuk dojki i aksila',
        'Ultrazvuk mekih tkiva i mišićno-skeletnih struktura',
        'Ultrazvuk zglobova (rame, koleno, kuk, ručni zglob, skočni zglob)',
        'Ultrazvuk kukova kod beba (skrining razvojnog displazije)',
        'Ultrazvuk CNS-a kod beba (transfontanelno)',
        'Ultrazvuk testisa i skrotuma',
        'Ultrazvuk limfnih čvorova',
      ],
      symptoms: [
        'Bol u trbuhu ili ispod desnog rebarnog luka',
        'Otok, čvor ili neobičan nalaz na dojci ili štitnoj žlezdi',
        'Poremećaj menstruacije, bolovi u maloj karlici',
        'Palpabilni čvor u vratu, aksilama ili preponama',
        'Bol i otok u zglobu (rame, koleno, kuk)',
        'Kontrolni pregled beba u prva 3 meseca života',
        'Prisustvo krvi u urinu, otežano mokrenje',
        'Uvećani limfni čvorovi',
      ],
      preparation: 'Za ultrazvuk abdomena preporučuje se 6–8 sati bez hrane (voda dozvoljena). Za pregled male karlice ili prostate potrebna je puna mokraćna bešika (0,5–1 L vode sat vremena pre pregleda). Za ultrazvuk štitne žlezde, dojki, mekih tkiva i zglobova nije potrebna nikakva priprema. Sa sobom ponesite eventualne prethodne nalaze.',
      duration: '15–20 minuta',
      report: 'Pisani nalaz sa slikama dobijate odmah nakon pregleda.',
      body: `<h2>Šta je ultrazvučna dijagnostika i kada je indikovana</h2>
<p>Ultrazvučni pregled (ehosonografija) je neinvazivna dijagnostička metoda koja koristi zvučne talase visoke frekvencije za prikazivanje unutrašnjih organa i struktura. Za razliku od rendgena i CT-a, ultrazvuk <strong>ne koristi jonizujuće zračenje</strong>, što ga čini metodom prvog izbora u pregledu dece, trudnica i za kontrole kod hroničnih pacijenata. Pregled je potpuno bezbolan i traje 15–20 minuta.</p>
<p>U partnerskoj dijagnostičkoj ordinaciji na Bulevaru umetnosti 15 (Novi Beograd) radimo kompletan spektar ultrazvučnih pregleda — od klasičnog ultrazvuka abdomena i štitne žlezde, preko dojki i mekih tkiva, do mišićno-skeletnog ultrazvuka i pregleda kukova kod beba.</p>

<h2>Ultrazvuk abdomena — jetra, žuč, bubrezi, pankreas</h2>
<p>Ultrazvuk abdomena je najčešće traženi pregled i osnovna kardiokoloska dijagnostika za bol u trbuhu, mučninu, žuticu, gubitak telesne mase, hipertenziju, poremećaje varenja i sumnju na kamen u žuči ili bubregu. Vizuelizuju se: <strong>jetra, žučna kesa i žučni putevi, pankreas, slezina, bubrezi, mokraćna bešika i velika krvna sudovi</strong> (aorta, donja šuplja vena). Otkriva ciste, kalkuluse, masnu jetru (steatoza), tumore, apscese i vaskularne anomalije.</p>

<h3>Ultrazvuk male karlice</h3>
<p>Kod žena — pregled materice i jajnika, otkrivanje mioma, cisti, endometrioze i praćenje ovulacije. Kod muškaraca — ultrazvuk prostate i mokraćne bešike. Za obe grupe pregled zahteva punu mokraćnu bešiku.</p>

<h2>Ultrazvuk štitne žlezde i vrata</h2>
<p>Ultrazvuk štitne žlezde je najosetljivija metoda za detekciju čvorova (nodula), Hashimoto tiroiditisa, cisti i tumorskih promena. Preporučuje se svima sa <strong>palpabilnim čvorom, poremećenim TSH/T3/T4 nalazom, porodičnom istorijom tiroidnih oboljenja</strong> ili nakon zračenja glave i vrata. Uz štitnu žlezdu vizualizuju se i vratni limfni čvorovi.</p>

<h2>Ultrazvuk dojki — mlade žene i skrining</h2>
<p>Ultrazvuk dojki je metoda izbora kod žena <strong>mlađih od 40 godina</strong> i tokom trudnoće/dojenja, kada je gustina žlezdanog tkiva visoka i mamografija manje osetljiva. Detektuje ciste, fibroadenome, promene sumnjive na karcinom i uvećane aksilarne limfne čvorove. Kombinuje se sa mamografijom kod žena starijih od 40 godina.</p>

<h2>Muskuloskeletni ultrazvuk — zglobovi, mišići, tetive</h2>
<p>Ultrazvučni pregled ramena, kolena, kuka, lakta, ručnog i skočnog zgloba je izuzetno vredan u sportskoj medicini i traumatologiji. Prikazuje: <strong>rupture tetiva rotatorne manžetne, meniskusne lezije (delimično), Bakerovu cistu, upale burzi (burzitis), tendinitise, ligamentarne povrede, Ahilovu tetivu, plantarnu fasciju, ganglione i strana tela u mekim tkivima</strong>. Odlično se dopunjuje sa MR pregledom i često je dovoljan za postavljanje dijagnoze plantarnog fasciitisa, teniskog i golferskog lakta, kalcifikujućeg tendinitisa ramena.</p>

<h2>Ultrazvuk kukova kod beba — obavezni skrining</h2>
<p>Prema preporukama pedijatara i ortopeda, <strong>svako novorođenče treba da uradi ultrazvuk kukova između 4. i 8. nedelje života</strong>, radi rane detekcije razvojne displazije kuka (DDH). Rano otkrivanje omogućava jednostavan tretman ortopedskim jastukom i sprečava kasnije komplikacije. Uz kukove često radimo i ultrazvuk CNS-a (transfontanelno) i abdomena kod beba.</p>

<h2>Prednosti ultrazvuka nad drugim metodama</h2>
<ul>
<li><strong>Bez zračenja</strong> — bezbedan i za trudnice i decu.</li>
<li><strong>Dinamičan pregled</strong> — moguće je pratiti pokret zgloba, protok krvi i disanje u realnom vremenu.</li>
<li><strong>Nalaz odmah</strong> — pisani izveštaj se izdaje po završetku pregleda.</li>
<li><strong>Pristupačna cena</strong> — od 5.000 RSD, bez uputa i čekanja.</li>
<li><strong>Nastavak terapije</strong> — direktno povezivanje sa fizikalnom terapijom i rehabilitacijom u PHYSIOWRLD-u.</li>
</ul>

<h2>Šta uraditi nakon ultrazvučnog nalaza</h2>
<p>Ukoliko nalaz ultrazvuka ukaže na muskuloskeletni problem (bol u ramenu, tenisski lakat, plantarni fasciitis, patelarnu tendinopatiju, Ahilovu tendinopatiju), preporučujemo direktan nastavak terapije u PHYSIOWRLD-u putem <strong>shockwave terapije, laser terapije, kineziterapije ili Active Release tehnike</strong>. Kompletna dijagnoza + terapija na jednom mestu skraćuje put pacijenta do oporavka.</p>`,
      pricing: [
        ['Ultrazvuk abdomena', '5.000 RSD'],
        ['Abdomen + mala karlica', '6.000 RSD'],
        ['Ultrazvuk štitne žlezde', '5.000 RSD'],
        ['Ultrazvuk dojki i aksila', '5.000 RSD'],
        ['Ultrazvuk mekih tkiva i zglobova', '5.000 RSD'],
        ['Ultrazvuk kukova kod beba', '5.000 RSD'],
        ['Ultrazvuk CNS-a kod beba (transfontanelno)', '5.000 RSD'],
        ['Ultrazvuk testisa', '5.000 RSD'],
      ],
      faqs: [
        { q: 'Koliko košta ultrazvuk u Novom Beogradu?', a: 'Cena ultrazvučnog pregleda kreće se od 5.000 RSD za pregled jedne regije (abdomen, štitna žlezda, dojke, meka tkiva, zglobovi). Kombinovani pregled abdomena i male karlice je 6.000 RSD.' },
        { q: 'Da li je potrebna priprema za ultrazvuk?', a: 'Za ultrazvuk abdomena preporučuje se 6–8 sati bez hrane. Za pregled male karlice ili prostate potrebna je puna mokraćna bešika. Za štitnu žlezdu, dojke, meka tkiva i zglobove priprema nije potrebna.' },
        { q: 'Kada dobijam nalaz ultrazvuka?', a: 'Pisani nalaz sa slikama dobijate odmah nakon završetka pregleda. Radiolog usmeno objašnjava nalaz i predlaže dalje korake.' },
        { q: 'Koliko dugo traje ultrazvučni pregled?', a: 'Standardni pregled traje 15–20 minuta. Kombinovani pregledi (npr. abdomen + mala karlica) traju do 30 minuta.' },
        { q: 'Da li se radi ultrazvuk kukova kod beba?', a: 'Da. Skrining ultrazvuka kukova radi se kod novorođenčadi između 4. i 8. nedelje života radi rane detekcije razvojne displazije kuka.' },
        { q: 'Da li je ultrazvuk bezbedan u trudnoći?', a: 'Da. Ultrazvuk ne koristi jonizujuće zračenje i potpuno je bezbedan tokom trudnoće za majku i plod.' },
        { q: 'Da li je potrebno uput lekara?', a: 'Nije potrebno uput. Pregled zakazujete direktno telefonom, WhatsApp-om ili preko online formulara.' },
        { q: 'Kako se zakazuje ultrazvuk?', a: 'Pozovite +381 60 503 7477, pošaljite WhatsApp poruku ili popunite formular. Termin dobijate najčešće u roku od 24–48h.' },
        { q: 'Ultrazvuk cena — koliko košta ultrazvučni pregled?', a: 'Ultrazvuk cena na Novom Beogradu kreće se od 5.000 RSD po regiji. Ultrazvuk abdomena — 5.000 RSD, ultrazvuk štitne žlezde — 5.000 RSD, ultrazvuk dojki — 5.000 RSD, ultrazvuk kukova kod beba — 5.000 RSD, kombinovani abdomen + mala karlica — 6.000 RSD. Cena uključuje pregled, pisani nalaz i slike.' },
        { q: 'Koliko košta ultrazvuk abdomena u Novom Beogradu?', a: 'Ultrazvuk abdomena na Novom Beogradu (Bulevar umetnosti 15) košta 5.000 RSD. Pregled obuhvata jetru, žučnu kesu, pankreas, slezinu, bubrege i mokraćnu bešiku. Nalaz dobijate odmah.' },
        { q: 'Koliko košta ultrazvuk štitne žlezde?', a: 'Ultrazvuk štitne žlezde košta 5.000 RSD. Uključuje procenu tkiva štitne žlezde, čvorova i limfnih čvorova vrata. Bez pripreme, bez uputa.' },
        { q: 'Koliko košta ultrazvuk dojki?', a: 'Ultrazvuk dojki i aksila je 5.000 RSD. Preporučuje se ženama do 40 godina i u trudnoći/laktaciji kao primarna metoda skrininga.' },
        { q: 'Koliko košta ultrazvuk kukova bebe?', a: 'Ultrazvuk kukova kod beba (skrining razvojne displazije) je 5.000 RSD. Radi se između 4. i 8. nedelje života.' },
        { q: 'Gde raditi ultrazvuk na Novom Beogradu?', a: 'Ultrazvučni pregled na Novom Beogradu radimo u partnerskoj ordinaciji na Bulevaru umetnosti 15 (blok 22), 5 minuta od Delta City-ja. Iskusni radiolozi, aparati visoke rezolucije, cena od 5.000 RSD i nalaz odmah.' },
        { q: 'Da li je ultrazvuk skuplji vikendom ili uveče?', a: 'Ne. Cena ultrazvučnog pregleda je ista bez obzira na dan ili vreme termina.' },
      ],
    },
    en: {
      title: 'Ultrasound New Belgrade — abdomen, thyroid, breasts, joints | from 5,000 RSD',
      metaDesc: 'Ultrasound diagnostics in New Belgrade — abdomen, small pelvis, thyroid, breasts, soft tissue, joints, infant hip screening. From 5,000 RSD, report issued immediately.',
      h1: 'Ultrasound New Belgrade — complete ultrasound diagnostics',
      name: 'Ultrasound diagnostics',
      short: 'Painless, radiation-free imaging on modern high-resolution machines.',
      intro: 'Ultrasound at our partner clinic on Bulevar umetnosti 15 is performed by experienced radiologists on modern high-resolution machines. No radiation, no pain, written report issued immediately after the exam. From 5,000 RSD.',
      priceLabel: 'from 5,000 RSD',
      keywords: 'ultrasound New Belgrade, ultrasound price, abdominal ultrasound, thyroid ultrasound, breast ultrasound, infant hip ultrasound, musculoskeletal ultrasound',
      indications: [
        'Abdominal ultrasound (liver, gallbladder, pancreas, spleen, kidneys)',
        'Small pelvis (uterus, ovaries, bladder, prostate)',
        'Thyroid and neck ultrasound',
        'Breast and axillary ultrasound',
        'Soft tissue and musculoskeletal ultrasound',
        'Joint ultrasound (shoulder, knee, hip, wrist, ankle)',
        'Infant hip ultrasound (DDH screening)',
        'Transfontanellar CNS ultrasound in infants',
        'Testicular and scrotal ultrasound',
        'Lymph node ultrasound',
      ],
      symptoms: [
        'Abdominal pain or right upper quadrant pain',
        'Palpable lump in breast, thyroid or neck',
        'Menstrual irregularities, pelvic pain',
        'Enlarged lymph nodes',
        'Joint pain and swelling (shoulder, knee, hip)',
        'Newborn 4–8 weeks — routine hip screening',
        'Hematuria, difficult urination',
      ],
      preparation: 'Abdominal ultrasound requires 6–8 hours fasting (water allowed). Small pelvis or prostate exam requires a full bladder (drink 0.5–1 L water 1 hour before). Thyroid, breast, soft tissue and joint ultrasound require no preparation. Please bring any prior imaging.',
      duration: '15–20 minutes',
      report: 'Written report with images issued immediately after the exam.',
      body: `<h2>What ultrasound diagnostics are and when they are indicated</h2>
<p>Ultrasound (sonography) is a non-invasive imaging method that uses high-frequency sound waves to visualize internal organs and structures. Unlike X-ray and CT, ultrasound <strong>does not use ionizing radiation</strong>, making it the first-line method for children, pregnant patients and repeated follow-ups. The exam is completely painless and takes 15–20 minutes.</p>
<p>At the partner diagnostic clinic on Bulevar umetnosti 15 (New Belgrade) we perform the complete spectrum of ultrasound exams — abdomen, thyroid, breasts, soft tissue, musculoskeletal ultrasound and infant hip screening.</p>

<h2>Abdominal ultrasound — liver, gallbladder, kidneys, pancreas</h2>
<p>Abdominal ultrasound is the most requested exam and the primary work-up for abdominal pain, nausea, jaundice, weight loss, hypertension, digestive issues and suspicion of gallstones or kidney stones. Structures visualized: <strong>liver, gallbladder and biliary tree, pancreas, spleen, kidneys, urinary bladder, aorta and inferior vena cava</strong>. Detects cysts, stones, fatty liver (steatosis), tumors, abscesses and vascular anomalies.</p>

<h3>Small pelvis ultrasound</h3>
<p>In women — uterus and ovaries: detection of fibroids, cysts, endometriosis and ovulation monitoring. In men — prostate and bladder. A full bladder is required.</p>

<h2>Thyroid and neck ultrasound</h2>
<p>The most sensitive method for detection of thyroid nodules, Hashimoto thyroiditis, cysts and malignancy. Indicated for anyone with <strong>a palpable lump, abnormal TSH/T3/T4, family history of thyroid disease</strong> or after head/neck irradiation. Cervical lymph nodes are visualized simultaneously.</p>

<h2>Breast ultrasound — younger women and screening</h2>
<p>Breast ultrasound is the method of choice for women <strong>under 40</strong> and during pregnancy/lactation, when glandular density is high and mammography less sensitive. Detects cysts, fibroadenomas, malignancy-suspicious lesions and enlarged axillary lymph nodes. Complementary to mammography over 40.</p>

<h2>Musculoskeletal ultrasound — joints, muscles, tendons</h2>
<p>Shoulder, knee, hip, elbow, wrist and ankle ultrasound is highly valuable in sports medicine and traumatology. Shows: <strong>rotator cuff tears, partial meniscal lesions, Baker's cyst, bursitis, tendinopathies, ligament injury, Achilles tendon, plantar fascia, ganglia and foreign bodies</strong>. Excellent complement to MRI and often sufficient to diagnose plantar fasciitis, tennis/golfer's elbow and calcific rotator cuff tendinitis.</p>

<h2>Infant hip ultrasound — mandatory screening</h2>
<p>Per pediatric and orthopedic guidelines, <strong>every newborn should have hip ultrasound between weeks 4 and 8</strong> to detect developmental hip dysplasia (DDH). Early detection allows simple treatment with an orthopedic pillow and prevents later complications. Often combined with transfontanellar CNS ultrasound.</p>

<h2>Advantages of ultrasound over other methods</h2>
<ul>
<li><strong>No radiation</strong> — safe in pregnancy and children.</li>
<li><strong>Dynamic exam</strong> — joint motion, blood flow and breathing tracked in real time.</li>
<li><strong>Immediate report</strong> — written findings issued at the end of the exam.</li>
<li><strong>Affordable</strong> — from 5,000 RSD, no referral, no waiting.</li>
<li><strong>Continuity of care</strong> — direct handover to physiotherapy and rehabilitation at PHYSIOWRLD.</li>
</ul>

<h2>What to do after an ultrasound report</h2>
<p>If ultrasound reveals a musculoskeletal issue (shoulder pain, tennis elbow, plantar fasciitis, patellar tendinopathy, Achilles tendinopathy), we recommend direct follow-up with <strong>shockwave therapy, laser therapy, kinesitherapy or Active Release Technique</strong> at PHYSIOWRLD.</p>`,
      pricing: [
        ['Abdominal ultrasound', '5,000 RSD'],
        ['Abdomen + small pelvis', '6,000 RSD'],
        ['Thyroid ultrasound', '5,000 RSD'],
        ['Breast & axilla ultrasound', '5,000 RSD'],
        ['Soft tissue & joint ultrasound', '5,000 RSD'],
        ['Infant hip ultrasound', '5,000 RSD'],
        ['Transfontanellar CNS (infants)', '5,000 RSD'],
        ['Testicular ultrasound', '5,000 RSD'],
      ],
      faqs: [
        { q: 'How much does ultrasound cost in New Belgrade?', a: 'Ultrasound starts at 5,000 RSD per region (abdomen, thyroid, breasts, soft tissue, joints). Combined abdomen + small pelvis is 6,000 RSD.' },
        { q: 'Is preparation required for ultrasound?', a: 'Abdominal ultrasound requires 6–8h fasting. Small pelvis / prostate requires a full bladder. Thyroid, breast, soft tissue and joint ultrasound need no preparation.' },
        { q: 'When do I get the report?', a: 'Written report with images is issued immediately after the exam. The radiologist explains the findings and recommends next steps.' },
        { q: 'How long does the exam take?', a: 'A standard exam takes 15–20 minutes. Combined exams up to 30 minutes.' },
        { q: 'Do you perform infant hip ultrasound?', a: 'Yes. Hip screening for newborns between weeks 4 and 8 to detect developmental dysplasia.' },
        { q: 'Is ultrasound safe in pregnancy?', a: 'Yes. Ultrasound uses no ionizing radiation and is completely safe during pregnancy.' },
        { q: 'Do I need a referral?', a: 'No referral required. Book directly by phone, WhatsApp or online form.' },
        { q: 'How to book?', a: 'Call +381 60 503 7477, message us on WhatsApp or fill out the online form. Appointments usually within 24–48h.' },
        { q: 'Ultrasound price — how much does it cost in New Belgrade?', a: 'Ultrasound price in New Belgrade starts at 5,000 RSD per region. Abdominal, thyroid, breast, joint and infant hip ultrasound are all 5,000 RSD; combined abdomen + small pelvis is 6,000 RSD.' },
        { q: 'Where can I get an ultrasound in New Belgrade?', a: 'Ultrasound is performed at the partner clinic on Bulevar umetnosti 15 (block 22, New Belgrade), 5 minutes from Delta City — experienced radiologists, from 5,000 RSD, report issued on the spot.' },
      ],
    },
    ru: {
      title: 'УЗИ Новый Белград — брюшная полость, щитовидка, суставы | от 5 000 RSD',
      metaDesc: 'УЗИ-диагностика в Новом Белграде — брюшная полость, малый таз, щитовидная железа, молочные железы, мягкие ткани, суставы, тазобедренные суставы у младенцев. От 5 000 RSD.',
      h1: 'УЗИ Новый Белград — полная ультразвуковая диагностика',
      name: 'УЗИ-диагностика',
      short: 'Безболезненное УЗИ без облучения на современных аппаратах высокого разрешения.',
      intro: 'УЗИ в партнёрской клинике на Bulevar umetnosti 15 выполняют опытные рентгенологи на современных аппаратах высокого разрешения. Без облучения, без боли, письменное заключение сразу после осмотра. От 5 000 RSD.',
      priceLabel: 'от 5 000 RSD',
      keywords: 'УЗИ Новый Белград, УЗИ цена, УЗИ брюшной полости, УЗИ щитовидной железы, УЗИ молочных желёз, УЗИ суставов, УЗИ тазобедренных суставов младенцы',
      indications: [
        'УЗИ брюшной полости (печень, желчный пузырь, поджелудочная, селезёнка, почки)',
        'УЗИ малого таза (матка, яичники, мочевой пузырь, простата)',
        'УЗИ щитовидной железы и шеи',
        'УЗИ молочных желёз и подмышечных лимфоузлов',
        'УЗИ мягких тканей и опорно-двигательного аппарата',
        'УЗИ суставов (плечо, колено, тазобедренный, лучезапястный, голеностопный)',
        'УЗИ тазобедренных суставов у младенцев (скрининг DDH)',
        'Нейросонография у младенцев (через родничок)',
        'УЗИ яичек и мошонки',
        'УЗИ лимфоузлов',
      ],
      symptoms: [
        'Боль в животе или в правом подреберье',
        'Пальпируемое образование в молочной железе, щитовидке или на шее',
        'Нарушения менструального цикла, боли в малом тазу',
        'Увеличенные лимфоузлы',
        'Боль и отёк сустава',
        'Плановый скрининг у младенцев 4–8 недель',
      ],
      preparation: 'УЗИ брюшной полости — натощак 6–8 часов (воду можно). УЗИ малого таза или простаты — полный мочевой пузырь. Щитовидная железа, молочные железы, мягкие ткани и суставы — без подготовки.',
      duration: '15–20 минут',
      report: 'Письменное заключение со снимками — сразу после осмотра.',
      body: `<h2>Что такое УЗИ и когда оно показано</h2>
<p>Ультразвуковое исследование — неинвазивный метод визуализации с использованием звуковых волн высокой частоты. В отличие от рентгена и КТ, УЗИ <strong>не использует ионизирующее излучение</strong> и безопасно для детей и беременных. Осмотр безболезненный и длится 15–20 минут.</p>

<h2>УЗИ брюшной полости</h2>
<p>Основное обследование при болях в животе, тошноте, желтухе, потере веса, подозрении на камни в желчном или почках. Визуализируются: <strong>печень, желчный пузырь, поджелудочная железа, селезёнка, почки, мочевой пузырь, аорта</strong>. Выявляет кисты, камни, жировой гепатоз, опухоли и сосудистые аномалии.</p>

<h2>УЗИ щитовидной железы</h2>
<p>Самый чувствительный метод обнаружения узлов, тиреоидита Хашимото и кист. Показан при пальпируемом узле, изменениях ТТГ/Т3/Т4, семейной истории или после облучения головы/шеи.</p>

<h2>УЗИ молочных желёз</h2>
<p>Метод выбора у женщин младше 40 лет и в период беременности/лактации. Обнаруживает кисты, фиброаденомы, подозрительные образования и увеличенные подмышечные лимфоузлы.</p>

<h2>УЗИ суставов и мягких тканей</h2>
<p>Плечо, колено, тазобедренный, локтевой, лучезапястный, голеностопный сустав. Показывает разрывы вращательной манжеты, кисту Бейкера, бурситы, тендинопатии, повреждения связок, ахилл, плантарную фасцию.</p>

<h2>УЗИ тазобедренных суставов у младенцев</h2>
<p>По рекомендации педиатров — <strong>всем новорождённым между 4-й и 8-й неделей</strong> для раннего выявления дисплазии тазобедренных суставов (DDH). Раннее выявление позволяет простое лечение и предотвращает осложнения.</p>

<h2>Что делать после заключения УЗИ</h2>
<p>Если УЗИ выявляет проблему опорно-двигательного аппарата (боль в плече, теннисный локоть, плантарный фасциит, тендинопатия), мы предлагаем прямое продолжение терапии в PHYSIOWRLD — <strong>ударно-волновая терапия, лазер, кинезиотерапия, Active Release</strong>.</p>`,
      pricing: [
        ['УЗИ брюшной полости', '5 000 RSD'],
        ['Брюшная полость + малый таз', '6 000 RSD'],
        ['УЗИ щитовидной железы', '5 000 RSD'],
        ['УЗИ молочных желёз и подмышек', '5 000 RSD'],
        ['УЗИ мягких тканей и суставов', '5 000 RSD'],
        ['УЗИ тазобедренных суставов у младенцев', '5 000 RSD'],
      ],
      faqs: [
        { q: 'Сколько стоит УЗИ в Новом Белграде?', a: 'УЗИ — от 5 000 RSD за одну область. Комбинированное УЗИ брюшной полости и малого таза — 6 000 RSD.' },
        { q: 'Нужна ли подготовка?', a: 'УЗИ брюшной полости — натощак 6–8 часов. Малый таз — полный мочевой пузырь. Щитовидная железа и суставы — без подготовки.' },
        { q: 'Когда я получу заключение?', a: 'Письменное заключение выдаётся сразу после осмотра.' },
        { q: 'Сколько длится осмотр?', a: '15–20 минут.' },
        { q: 'Делаете ли УЗИ младенцам?', a: 'Да. Скрининг тазобедренных суставов и нейросонография.' },
        { q: 'Безопасно ли УЗИ при беременности?', a: 'Полностью безопасно.' },
        { q: 'Нужно ли направление?', a: 'Нет.' },
        { q: 'Как записаться?', a: 'Позвоните +381 60 503 7477 или через WhatsApp.' },
        { q: 'УЗИ цена — сколько стоит в Новом Белграде?', a: 'Цена УЗИ в Новом Белграде — от 5 000 RSD за одну область. Брюшная полость, щитовидная железа, молочные железы, суставы и тазобедренные суставы младенцев — 5 000 RSD; брюшная полость + малый таз — 6 000 RSD.' },
        { q: 'Где сделать УЗИ в Новом Белграде?', a: 'УЗИ выполняется в партнёрской клинике на Bulevar umetnosti 15 (блок 22, Новый Белград) — опытные радиологи, заключение сразу.' },
      ],
    },
  },

  // ────────────────────────── KOLOR DOPLER ──────────────────────────
  {
    slug: 'kolor-dopler-novi-beograd',
    anchor: 'dopler',
    image: '/images/diagnostika-dopler.jpg',
    fromPrice: '5500',
    icon: 'Waves',
    sr: {
      title: 'Kolor dopler Novi Beograd — vratni, ekstremiteti, transkranijalni | od 5.500 RSD',
      metaDesc: 'Kolor dopler krvnih sudova u Novom Beogradu — karotide, gornji i donji ekstremiteti, renalne arterije, transkranijalni dopler. Cena od 5.500 RSD, nalaz odmah.',
      h1: 'Kolor dopler Novi Beograd — dijagnostika krvnih sudova',
      name: 'Kolor Dopler',
      short: 'Precizna procena protoka krvi u arterijama i venama.',
      intro: 'Kolor dopler pregled u partnerskoj ordinaciji na Bulevaru umetnosti 15 obavljaju iskusni vaskularni specijalisti. Rana detekcija ateroskleroze, tromboze, stenoze i venske insuficijencije. Cena od 5.500 RSD.',
      priceLabel: 'od 5.500 RSD',
      keywords: 'kolor dopler Novi Beograd, dopler karotida cena, dopler krvnih sudova nogu, transkranijalni dopler, dopler renalnih arterija, dopler aorte, tromboza dopler',
      indications: [
        'Dopler krvnih sudova vrata (karotide, vertebralne arterije)',
        'Dopler krvnih sudova glave — transkranijalni',
        'Dopler donjih ekstremiteta (arterije i vene)',
        'Dopler gornjih ekstremiteta',
        'Dopler renalnih arterija',
        'Dopler trbušne aorte',
        'Dopler portalnog sistema',
        'Dopler krvnih sudova testisa (varikocela)',
        'Dopler penilne regije',
        'Dopler krvnih sudova male karlice',
      ],
      symptoms: [
        'Vrtoglavica, glavobolja, poremećaji sluha i vida',
        'Bol u nogama pri hodanju (klaudikacije)',
        'Otok, bol i crvenilo potkolenice (sumnja na TVT)',
        'Hladne, blede noge, ne osećaju se pulsevi',
        'Hipertenzija koja ne reaguje na terapiju',
        'Prisustvo proširenih vena (variksa)',
        'Muška neplodnost, sumnja na varikocelu',
        'Prethodni šlog ili TIA',
        'Pušački status i porodična istorija ateroskleroze',
      ],
      preparation: 'Za dopler karotida, ekstremiteta i transkranijalni dopler priprema nije potrebna. Za dopler trbušne aorte i renalnih arterija preporučuje se 6–8 sati bez hrane. Za dopler portalnog sistema takođe se preporučuje gladovanje. Nosite udobnu odeću koja se lako uklanja sa pregledane regije.',
      duration: '20–30 minuta',
      report: 'Pisani nalaz sa merenjima protoka i indeksa dobijate odmah.',
      body: `<h2>Šta je kolor dopler pregled</h2>
<p>Kolor dopler ultrasonografija je specijalizovana ultrazvučna metoda koja koristi Doplerov efekat za vizualizaciju i merenje <strong>protoka krvi u arterijama i venama u realnom vremenu</strong>. Različite boje označavaju smer i brzinu protoka, dok spektralna analiza daje precizne numeričke vrednosti (PSV, EDV, RI, PI). Metoda je bezbolna, bez zračenja i traje 20–30 minuta.</p>

<h2>Dopler krvnih sudova vrata — karotide i vertebralne arterije</h2>
<p>Najčešće traženi dopler pregled. Otkriva <strong>aterosklerotske plakove, stenoze karotidnih arterija, disekciju i vaskularne anomalije</strong>. Obavezan je kod pacijenata koji su preživeli šlog ili TIA, kao i kao skrining kod hipertenzije, hiperlipidemije, dijabetesa i pušačkog statusa. Rana detekcija hemodinamski značajne stenoze (>50–70%) je ključna za prevenciju moždanog udara.</p>

<h3>Transkranijalni dopler (TCD)</h3>
<p>Merenje protoka u intrakranijalnim arterijama (srednja moždana arterija, prednja i zadnja cerebralna). Indikovan kod migrena, sumnji na vaskularnu insuficijenciju, embolusa, vazospazma nakon subarahnoidalne hemoragije i praćenja pacijenata sa anemijom srpastih ćelija.</p>

<h2>Dopler donjih ekstremiteta — arterije i vene</h2>
<p><strong>Arterijski dopler nogu</strong> je zlatni standard za dijagnozu periferne arterijske bolesti (PAB), tipične kod pacijenata sa dijabetesom, pušača i starijih od 60 godina. Otkriva stenoze, okluzije i meri se ABI indeks (ankle-brachial index).</p>
<p><strong>Venski dopler nogu</strong> otkriva <strong>duboku vensku trombozu (DVT)</strong> — hitno stanje sa rizikom od plućne embolije. Obavezan kod otoka jednog krata, bola, crvenila i toplote. Takođe se koristi u dijagnozi hronične venske insuficijencije, mapiranju variksa pre operacije i planiranju sklerooterapije.</p>

<h2>Dopler renalnih arterija i trbušne aorte</h2>
<p>Dopler renalnih arterija je indikovan kod <strong>rezistentne hipertenzije, iznenadnog pogoršanja funkcije bubrega</strong> ili pojave šuma nad abdomenom. Otkriva stenozu renalne arterije (najčešće aterosklerotsku ili fibromuskularnu displaziju). Dopler trbušne aorte skrining je za aneurizme abdominalne aorte kod muškaraca starijih od 65 godina i pušača.</p>

<h2>Dopler testisa i varikocela</h2>
<p>Dopler krvnih sudova testisa je zlatni standard u dijagnozi <strong>varikocele</strong> — jednog od najčešćih uzroka muške neplodnosti. Meri se protok u pleksusu pampiniformisu u miru i pri Valsalva manevru. Takođe se koristi u dijagnozi torzije testisa (urgentno stanje) i orhitis/epididimitis.</p>

<h2>Prednosti kolor doplera</h2>
<ul>
<li><strong>Bez zračenja i kontrastnih sredstava</strong> — bezbedan i za trudnice i bubrežne pacijente.</li>
<li><strong>Rana detekcija</strong> — otkriva stenoze i tromboze pre pojave simptoma.</li>
<li><strong>Merljivi rezultati</strong> — PSV, EDV, RI, ABI, procenat stenoze.</li>
<li><strong>Nalaz odmah</strong> — pisani izveštaj sa merenjima nakon pregleda.</li>
</ul>

<h2>Šta uraditi nakon dopler nalaza</h2>
<p>Ako se otkrije značajna stenoza karotidnih arterija, pacijenta upućujemo vaskularnom hirurgu radi ocene indikacija za endarterektomiju ili stenting. Kod otkrivene tromboze — hitno na dalju obradu i antikoagulantnu terapiju. Kod hronične venske insuficijencije — konsultacija za sklerooterapiju ili laser vena, uz preporuku fizikalne terapije, kompresivnih čarapa i limfnе drenaže u PHYSIOWRLD-u.</p>`,
      pricing: [
        ['Dopler vrata (karotide + vertebralne)', '5.500 RSD'],
        ['Dopler glave — transkranijalni', '6.000 RSD'],
        ['Dopler donjih ekstremiteta (arterije + vene)', '7.000 RSD'],
        ['Dopler gornjih ekstremiteta', '7.000 RSD'],
        ['Dopler renalnih arterija', '6.500 RSD'],
        ['Dopler trbušne aorte', '5.500 RSD'],
        ['Dopler portalnog sistema', '5.500 RSD'],
        ['Dopler testisa (varikocela)', '5.500 RSD'],
      ],
      faqs: [
        { q: 'Koliko košta kolor dopler?', a: 'Dopler karotidnih arterija (vrat) je 5.500 RSD, dopler donjih ili gornjih ekstremiteta 7.000 RSD, transkranijalni dopler 6.000 RSD, dopler renalnih arterija 6.500 RSD.' },
        { q: 'Šta pokazuje dopler karotida?', a: 'Otkriva aterosklerotske plakove, stenoze, okluzije, disekcije i vaskularne anomalije koje mogu dovesti do šloga.' },
        { q: 'Kada je dopler krvnih sudova obavezan?', a: 'Nakon preživljenog šloga ili TIA, kod rezistentne hipertenzije, dijabetesa, pušačkog statusa preko 30 godina, hiperlipidemije, ili kod simptoma poput vrtoglavice, bola u nogama pri hodanju i otoka jedne noge.' },
        { q: 'Da li je potrebna priprema za dopler?', a: 'Za dopler karotida, ekstremiteta i transkranijalni dopler nije potrebna. Za dopler trbušne aorte i renalnih arterija preporučuje se 6–8h bez hrane.' },
        { q: 'Koliko traje pregled?', a: '20–30 minuta zavisno od regije. Dopler celih nogu (arterije + vene) do 45 minuta.' },
        { q: 'Da li dopler može otkriti trombozu?', a: 'Da. Venski dopler donjih ekstremiteta je zlatni standard za dijagnozu duboke venske tromboze (DVT).' },
        { q: 'Da li se radi dopler testisa?', a: 'Da. Dopler testisa je zlatni standard u dijagnozi varikocele i sumnji na torziju testisa.' },
        { q: 'Kada dobijam nalaz?', a: 'Pisani nalaz sa svim merenjima dobijate odmah nakon pregleda.' },
        { q: 'Dopler pregled cena — koliko košta dopler u Novom Beogradu?', a: 'Dopler pregled cena na Novom Beogradu: dopler karotidnih arterija — 5.500 RSD, dopler vena i arterija nogu — 7.000 RSD, transkranijalni dopler — 6.000 RSD, dopler renalnih arterija — 6.500 RSD, dopler testisa (varikocela) — 5.500 RSD.' },
        { q: 'Koliko košta dopler nogu?', a: 'Dopler vena i arterija donjih ekstremiteta na Novom Beogradu košta 7.000 RSD i obuhvata kompletnu procenu venske i arterijske cirkulacije obe noge.' },
        { q: 'Koliko košta dopler karotida (vrata)?', a: 'Dopler karotidnih arterija (krvni sudovi vrata) na Novom Beogradu je 5.500 RSD. Preporučuje se nakon 40. godine i kod svih faktora rizika za šlog.' },
        { q: 'Gde raditi dopler krvnih sudova u Novom Beogradu?', a: 'Kolor dopler radimo na Bulevaru umetnosti 15 (Novi Beograd, blok 22) — vaskularni specijalisti, cena od 5.500 RSD, nalaz odmah.' },
        { q: 'Da li se radi dopler u trudnoći?', a: 'Da. Dopler krvnih sudova (vrat, noge) je bezbedan u trudnoći. Za fetalni dopler potrebna je konsultacija sa ginekologom.' },
      ],
    },
    en: {
      title: 'Color Doppler New Belgrade — carotids, extremities, transcranial | from 5,500 RSD',
      metaDesc: 'Color Doppler of blood vessels in New Belgrade — carotids, upper and lower extremities, renal arteries, transcranial Doppler. From 5,500 RSD, immediate report.',
      h1: 'Color Doppler New Belgrade — vascular diagnostics',
      name: 'Color Doppler',
      short: 'Precise blood flow assessment in arteries and veins.',
      intro: 'Color Doppler at our partner clinic on Bulevar umetnosti 15 is performed by experienced vascular specialists. Early detection of atherosclerosis, thrombosis, stenosis and venous insufficiency. From 5,500 RSD.',
      priceLabel: 'from 5,500 RSD',
      keywords: 'color Doppler Belgrade, carotid Doppler, leg Doppler, transcranial Doppler, renal artery Doppler, DVT ultrasound, varicocele Doppler',
      indications: [
        'Neck vessel Doppler (carotids, vertebrals)',
        'Transcranial Doppler',
        'Lower extremity Doppler (arteries & veins)',
        'Upper extremity Doppler',
        'Renal artery Doppler',
        'Abdominal aorta Doppler',
        'Portal system Doppler',
        'Testicular Doppler (varicocele)',
      ],
      symptoms: [
        'Dizziness, headache, hearing/vision changes',
        'Leg pain when walking (claudication)',
        'Swelling, pain and redness of the calf (suspected DVT)',
        'Cold, pale legs with weak pulses',
        'Resistant hypertension',
        'Varicose veins',
        'Male infertility (suspected varicocele)',
        'Previous stroke or TIA',
      ],
      preparation: 'No preparation for carotid, extremity or transcranial Doppler. Abdominal aorta and renal artery Doppler require 6–8h fasting. Wear comfortable clothes.',
      duration: '20–30 minutes',
      report: 'Written report with flow measurements issued immediately.',
      body: `<h2>What color Doppler is</h2>
<p>Color Doppler ultrasonography is a specialized ultrasound method that uses the Doppler effect to visualize and measure <strong>blood flow in arteries and veins in real time</strong>. Colors encode direction and velocity, spectral analysis provides numeric parameters (PSV, EDV, RI, PI). Painless, no radiation, 20–30 minutes.</p>

<h2>Carotid and vertebral Doppler</h2>
<p>The most common Doppler exam. Detects <strong>atherosclerotic plaques, stenoses, occlusions, dissection and anomalies</strong>. Mandatory after stroke or TIA and as screening in hypertension, hyperlipidemia, diabetes and smoking. Early detection of hemodynamically significant stenosis (>50–70%) is crucial for stroke prevention.</p>

<h3>Transcranial Doppler (TCD)</h3>
<p>Measures flow in intracranial arteries. Indicated in migraine, vertebrobasilar insufficiency, embolus detection, post-SAH vasospasm and sickle-cell monitoring.</p>

<h2>Lower extremity Doppler — arteries & veins</h2>
<p><strong>Arterial leg Doppler</strong> is the gold standard for peripheral arterial disease (PAD) in diabetics, smokers and over 60s. Measures ABI (ankle-brachial index).</p>
<p><strong>Venous leg Doppler</strong> detects <strong>deep vein thrombosis (DVT)</strong> — an emergency with pulmonary embolism risk. Mandatory for unilateral calf swelling, pain, redness. Also used in chronic venous insufficiency and varicose vein mapping.</p>

<h2>Renal artery and abdominal aorta Doppler</h2>
<p>Renal artery Doppler is indicated for <strong>resistant hypertension, sudden renal function decline</strong> or abdominal bruit. Detects renal artery stenosis. Aorta Doppler screens for abdominal aortic aneurysm in men over 65 and smokers.</p>

<h2>Testicular Doppler and varicocele</h2>
<p>Testicular Doppler is the gold standard for <strong>varicocele</strong> — a common cause of male infertility. Flow in the pampiniform plexus is measured at rest and during Valsalva. Also used for testicular torsion (emergency) and orchitis/epididymitis.</p>

<h2>After the Doppler report</h2>
<p>Significant carotid stenosis — vascular surgery consultation. Thrombosis — urgent anticoagulation. Chronic venous insufficiency — sclerotherapy or laser consult, and compression + lymphatic drainage at PHYSIOWRLD.</p>`,
      pricing: [
        ['Neck vessels (carotids + vertebrals)', '5,500 RSD'],
        ['Transcranial', '6,000 RSD'],
        ['Lower extremities', '7,000 RSD'],
        ['Upper extremities', '7,000 RSD'],
        ['Renal arteries', '6,500 RSD'],
        ['Abdominal aorta', '5,500 RSD'],
      ],
      faqs: [
        { q: 'How much does color Doppler cost?', a: 'Neck vessel Doppler 5,500 RSD, lower/upper extremity 7,000 RSD, transcranial 6,000 RSD, renal 6,500 RSD.' },
        { q: 'What does carotid Doppler show?', a: 'Atherosclerotic plaques, stenoses, occlusions, dissection and anomalies that may lead to stroke.' },
        { q: 'When is Doppler mandatory?', a: 'After stroke/TIA, resistant hypertension, diabetes, smoking >30 years, hyperlipidemia, dizziness, claudication, unilateral leg swelling.' },
        { q: 'Is preparation required?', a: 'No preparation for carotid, extremity or transcranial. Abdominal aorta / renal Doppler — 6–8h fasting.' },
        { q: 'How long does it take?', a: '20–30 min; full leg (arteries + veins) up to 45 min.' },
        { q: 'Can Doppler detect DVT?', a: 'Yes — the gold standard for deep vein thrombosis.' },
        { q: 'Testicular Doppler?', a: 'Yes — the gold standard for varicocele and testicular torsion.' },
        { q: 'When is the report ready?', a: 'Immediately after the exam.' },
        { q: 'Doppler ultrasound price in New Belgrade?', a: 'Carotid Doppler 5,500 RSD, leg Doppler 7,000 RSD, transcranial 6,000 RSD, renal 6,500 RSD, testicular 5,500 RSD — at the partner clinic on Bulevar umetnosti 15.' },
        { q: 'Where can I get a Doppler scan in New Belgrade?', a: 'Color Doppler is performed at the partner vascular clinic on Bulevar umetnosti 15 (block 22, New Belgrade). From 5,500 RSD, immediate report.' },
      ],
    },
    ru: {
      title: 'Цветной допплер Новый Белград — сонные, конечности | от 5 500 RSD',
      metaDesc: 'Цветной допплер сосудов в Новом Белграде — сонные артерии, конечности, почечные артерии, транскраниальный допплер. От 5 500 RSD, заключение сразу.',
      h1: 'Цветной допплер Новый Белград — сосудистая диагностика',
      name: 'Цветной допплер',
      short: 'Точная оценка кровотока в артериях и венах.',
      intro: 'Цветной допплер в партнёрской клинике на Bulevar umetnosti 15 выполняют опытные сосудистые специалисты. Раннее выявление атеросклероза, тромбоза, стеноза. От 5 500 RSD.',
      priceLabel: 'от 5 500 RSD',
      keywords: 'цветной допплер Новый Белград, допплер сонных артерий, допплер ног, транскраниальный допплер, ТГВ УЗИ',
      indications: [
        'Допплер сосудов шеи (сонные, позвоночные)',
        'Транскраниальный допплер',
        'Допплер нижних конечностей (артерии + вены)',
        'Допплер верхних конечностей',
        'Допплер почечных артерий',
        'Допплер брюшной аорты',
        'Допплер портальной системы',
        'Допплер яичек (варикоцеле)',
      ],
      symptoms: [
        'Головокружение, головная боль',
        'Боль в ногах при ходьбе',
        'Отёк и покраснение голени (подозрение на ТГВ)',
        'Резистентная гипертензия',
        'Варикозное расширение вен',
        'Мужское бесплодие',
      ],
      preparation: 'Допплер сонных артерий и конечностей — без подготовки. Допплер брюшной аорты и почечных артерий — натощак 6–8 ч.',
      duration: '20–30 минут',
      report: 'Письменное заключение с измерениями — сразу.',
      body: `<h2>Что такое цветной допплер</h2>
<p>Цветное допплеровское УЗИ — специализированный метод визуализации и измерения <strong>кровотока в артериях и венах в реальном времени</strong>. Безболезненно, без облучения, 20–30 минут.</p>

<h2>Допплер сонных артерий</h2>
<p>Обнаруживает атеросклеротические бляшки, стенозы, окклюзии. Обязателен после инсульта или ТИА, при гипертензии, диабете, курении.</p>

<h2>Допплер сосудов нижних конечностей</h2>
<p>Артериальный — золотой стандарт диагностики периферической артериальной болезни. Венозный — диагностика тромбоза глубоких вен.</p>

<h2>Допплер почечных артерий и аорты</h2>
<p>Показан при резистентной гипертензии, скрининг аневризмы аорты у мужчин старше 65 лет.</p>`,
      pricing: [
        ['Сосуды шеи', '5 500 RSD'],
        ['Транскраниальный', '6 000 RSD'],
        ['Нижние конечности', '7 000 RSD'],
        ['Верхние конечности', '7 000 RSD'],
        ['Почечные артерии', '6 500 RSD'],
      ],
      faqs: [
        { q: 'Сколько стоит допплер?', a: 'Сосуды шеи — 5 500 RSD, конечности — 7 000 RSD, транскраниальный — 6 000 RSD.' },
        { q: 'Что показывает допплер сонных?', a: 'Атеросклеротические бляшки, стенозы, окклюзии.' },
        { q: 'Нужна ли подготовка?', a: 'Для сонных артерий и конечностей — нет. Для аорты и почечных артерий — натощак 6–8 ч.' },
        { q: 'Обнаруживает ли тромбоз?', a: 'Да, венозный допплер — золотой стандарт для ТГВ.' },
        { q: 'Сколько длится?', a: '20–30 минут.' },
        { q: 'Когда заключение?', a: 'Сразу после осмотра.' },
        { q: 'Допплер цена в Новом Белграде?', a: 'Сонные — 5 500 RSD, ноги — 7 000 RSD, транскраниальный — 6 000, почечные — 6 500, тестикулярный — 5 500. Bulevar umetnosti 15.' },
      ],
    },
  },

  // ────────────────────────── RENDGEN (RTG) ──────────────────────────
  {
    slug: 'rendgen-novi-beograd',
    anchor: 'rendgen',
    image: '/images/diagnostika-rendgen.jpg',
    fromPrice: '3500',
    icon: 'Zap',
    medicalTestType: 'https://schema.org/MedicalImagingTechnique',
    sr: {
      title: 'Rendgen (RTG) Novi Beograd — pluća, kičma, ekstremiteti | od 3.500 RSD',
      metaDesc: 'Digitalni rendgen (RTG) na Novom Beogradu — pluća, kičma, ekstremiteti, zglobovi, abdomen. Cena od 3.500 RSD, nalaz istog dana, minimalna doza zračenja.',
      h1: 'Rendgen (RTG) Novi Beograd — digitalna rendgen dijagnostika',
      name: 'Rendgen dijagnostika (RTG)',
      short: 'Digitalni RTG snimci sa minimalnom dozom zračenja i brzim nalazom.',
      intro: 'Digitalni rendgen na Bulevaru umetnosti 15 — moderna aparata, minimalna doza zračenja, snimak i nalaz istog dana. Cena od 3.500 RSD.',
      priceLabel: 'od 3.500 RSD',
      keywords: 'rendgen Novi Beograd, RTG cena, digitalni rendgen, RTG pluća, RTG kičme, RTG kolena, RTG stopala, RTG kod dece',
      indications: [
        'RTG pluća (skrining, upala, tumor, TBC)',
        'RTG kičmenog stuba (vratna, grudna, slabinska)',
        'RTG ekstremiteta (traumatologija)',
        'RTG zglobova (rame, koleno, kuk, stopalo, šaka)',
        'RTG karlice i kukova',
        'RTG paranazalnih sinusa',
        'RTG lobanje',
        'RTG abdomena (nativni)',
        'Funkcionalni RTG kičme',
        'Kontrastni RTG pregledi',
      ],
      symptoms: [
        'Trauma, sumnja na prelom',
        'Suvi ili produktivni kašalj duže od 3 nedelje',
        'Bol u leđima ili vratu',
        'Bol u ramenu, kuku, kolenu ili stopalu',
        'Otok i deformitet zgloba',
        'Sumnja na skoliozu (kod dece i mladih)',
        'Preoperativna evaluacija',
        'Preventivni skrining pluća',
      ],
      preparation: 'Za većinu RTG pregleda priprema nije potrebna. Skinuti sve metalne predmete (nakit, sat, cirkone, pojas). Trudnice obavezno obavestiti lekara — RTG se u trudnoći radi samo u životno indikovanim situacijama. Za funkcionalne snimke kičme dolazak sa udobnom odećom.',
      duration: '10–15 minuta',
      report: 'Digitalni snimak i pisani nalaz radiologa istog dana (obično u roku od 1–2 sata).',
      body: `<h2>Šta je digitalni rendgen i kada je indikovan</h2>
<p>Digitalni rendgen (RTG) je najstarija ali i dalje najkorišćenija radiološka dijagnostička metoda. Koristi X-zrake za prikaz koštanih i mekih tkiva. Digitalni detektor omogućava <strong>4–10 puta manju dozu zračenja</strong> nego klasični RTG na filmu i mnogo brže procesiranje snimka. Nalaz je najčešće spreman istog dana.</p>

<h2>RTG pluća — najčešći RTG pregled</h2>
<p>RTG pluća (rendgen grudnog koša) je osnovna dijagnostika kod: <strong>suvog ili produktivnog kašlja dužeg od 3 nedelje, otežanog disanja, bola u grudima, sumnje na pneumoniju, bronhitis, TBC, plućnu emboliju, tumor, pleuralni izliv i pneumotoraks</strong>. Radi se u dve projekcije (PA i profil) za kompletnu procenu. Takođe je obavezan pre nekih operacija i za sistematske preglede.</p>

<h2>RTG kičmenog stuba</h2>
<p>RTG kičme se radi u projekcijama za odgovarajuću regiju: <strong>cervikalna, torakalna ili lumbosakralna kičma</strong>. Otkriva degenerativne promene (osteohondroza, spondiloza), skoliozu i kifozu, listezu (spondilolistezu), traumu (kompresivne prelome), tumore, osteoporotske promene i kongenitalne anomalije. <strong>Funkcionalni snimci u fleksiji i ekstenziji</strong> ključni su u proceni segmentalne nestabilnosti.</p>

<h3>Skolioza kod dece</h3>
<p>RTG kičme celokupno (full-spine, EOS-like) je zlatni standard za merenje Cobbovog ugla — ključnog parametra za dijagnozu i praćenje skolioze. Zajedno sa <a href="/korektivne-vezbe-za-decu-novi-beograd">korektivnim vežbama za decu</a> u PHYSIOWRLD-u čini kompletan dijagnostičko-terapijski put.</p>

<h2>RTG zglobova i ekstremiteta</h2>
<p>RTG zglobova je prva linija dijagnostike kod svake traume, artritisa i degenerativnih promena. Standardni pregledi: <strong>RTG ramena, lakta, ručnog zgloba, kuka, kolena, skočnog zgloba i stopala</strong>. Otkriva prelome, dislokacije, kalkanealne trnove (pete), hallux valgus, osteoartritis, artritis, osteohondrome i strana tela.</p>

<h3>Peta trn (calcaneal spur) i plantarni fasciitis</h3>
<p>RTG stopala u profilu je osnovna metoda za dijagnozu <strong>koštanog trna na peti (calcaneal spur)</strong>, koji se često nalazi uz plantarni fasciitis. Nakon dijagnoze preporučujemo <strong>shockwave terapiju</strong> u PHYSIOWRLD-u — dokazano efikasan tretman u više od 80% slučajeva.</p>

<h2>Bezbednost i doza zračenja</h2>
<p>Digitalni RTG snimak pluća daje dozu zračenja od <strong>~0.02 mSv</strong> — što je jednako oko 3 dana prirodne pozadinske radijacije. RTG ekstremiteta je još niža doza. Modern digitalni detektori omogućavaju <strong>ALARA princip</strong> (As Low As Reasonably Achievable) — najmanju moguću dozu za kvalitetan snimak.</p>

<h2>Kada RTG NIJE dovoljan i traži se MR ili CT</h2>
<ul>
<li>Meki tkivni problemi (meniskus, ligamenti, rotatorna manžetna) — <strong>MR</strong>.</li>
<li>Detalji intrakranijalnih promena, plućnih čvorova — <strong>CT</strong>.</li>
<li>Rana faza osteoartritisa i osteohondritisa — <strong>MR</strong>.</li>
<li>Diskus hernija sa neurološkim deficitom — <strong>MR lumbalne kičme</strong>.</li>
</ul>
<p>Naši radiolozi na osnovu RTG nalaza daju jasnu preporuku za dopunske preglede kada je to potrebno.</p>`,
      pricing: [
        ['RTG pluća (grudni koš)', '3.500 RSD'],
        ['RTG kičme (jedna regija)', 'od 8.000 RSD'],
        ['RTG ekstremiteta (jedan)', '3.500 RSD'],
        ['RTG zgloba (rame, koleno, kuk, stopalo, šaka)', '3.500 RSD'],
        ['RTG karlice', '3.500 RSD'],
        ['RTG paranazalnih sinusa', '3.500 RSD'],
        ['RTG lobanje', '3.500 RSD'],
        ['Funkcionalni RTG kičme', 'od 8.000 RSD'],
      ],
      faqs: [
        { q: 'Koliko košta rendgen u Novom Beogradu?', a: 'RTG pluća, ekstremiteta i zglobova košta 3.500 RSD. RTG kičme od 8.000 RSD (zavisno od broja projekcija i regije).' },
        { q: 'Da li postoji rendgen u PHYSIOWRLD-u?', a: 'RTG snimanje se obavlja u partnerskoj ordinaciji na Bulevaru umetnosti 15, a nalaz i konsultacija su deo jedinstvenog dijagnostičko-terapijskog puta.' },
        { q: 'Da li je RTG bezbedan?', a: 'Digitalni RTG koristi minimalnu dozu zračenja (npr. RTG pluća = ~0.02 mSv, jednako ~3 dana pozadinske radijacije). Bezbedan je za sve osim za trudnice.' },
        { q: 'Da li je potrebna priprema?', a: 'Ne. Samo skinuti metalne predmete (nakit, sat, pojas) sa regije koja se snima. Trudnice obavestiti lekara.' },
        { q: 'Kada dobijam nalaz RTG-a?', a: 'Digitalni snimak i pisani nalaz radiologa dobijate istog dana, obično u roku od 1–2 sata.' },
        { q: 'Kada je potreban MR umesto RTG-a?', a: 'RTG prikazuje kosti; za meniskus, ligamente, disk hernije i rane degenerativne promene potreban je MR.' },
        { q: 'Da li se radi RTG kičme za skoliozu?', a: 'Da. Full-spine RTG je zlatni standard za merenje Cobbovog ugla i dijagnozu skolioze kod dece i odraslih.' },
        { q: 'Da li se rade funkcionalni RTG snimci?', a: 'Da. Funkcionalni RTG cervikalne i lumbalne kičme u fleksiji i ekstenziji radi se za procenu segmentalne nestabilnosti.' },
        { q: 'Rendgen cena — koliko košta RTG snimak u Novom Beogradu?', a: 'Rendgen cena na Novom Beogradu: RTG pluća 3.500 RSD, RTG ruke ili noge 3.500 RSD, RTG zgloba (rame, lakat, koleno, skočni zglob) 3.500 RSD, RTG karlice 3.500 RSD, RTG cervikalne kičme 4.000 RSD, RTG celе kičme (full-spine) od 8.000 RSD. U cenu ulazi digitalni snimak i pisani nalaz radiologa.' },
        { q: 'Koliko košta RTG pluća u Novom Beogradu?', a: 'RTG pluća na Novom Beogradu (Bulevar umetnosti 15) košta 3.500 RSD. Snimak i nalaz radiologa istog dana.' },
        { q: 'Koliko košta RTG kolena, ramena ili skočnog zgloba?', a: 'RTG zgloba (rame, koleno, kuk, ručni ili skočni zglob) na Novom Beogradu košta 3.500 RSD po regiji, u 2 projekcije.' },
        { q: 'Koliko košta RTG kičme?', a: 'RTG cervikalne kičme 4.000 RSD, torakalne ili lumbalne od 4.500 RSD, full-spine (za skoliozu) od 8.000 RSD, funkcionalni RTG od 8.000 RSD.' },
        { q: 'Gde raditi rendgen u Novom Beogradu?', a: 'RTG snimanje na Novom Beogradu radimo u partnerskoj ordinaciji na Bulevaru umetnosti 15 (blok 22) — digitalni RTG, minimalna doza zračenja, nalaz istog dana, cena od 3.500 RSD.' },
        { q: 'Da li je rendgen skuplji ako se radi hitno?', a: 'Ne. Cena RTG snimanja je ista bez obzira na to da li je pregled zakazan unapred ili se radi istog dana.' },
      ],
    },
    en: {
      title: 'X-ray New Belgrade — chest, spine, extremities | from 3,500 RSD',
      metaDesc: 'Digital X-ray in New Belgrade — chest, spine, extremities, joints, abdomen. From 3,500 RSD, same-day report, minimal radiation dose.',
      h1: 'X-ray New Belgrade — digital X-ray diagnostics',
      name: 'X-ray diagnostics',
      short: 'Digital X-ray with minimal radiation dose and fast reporting.',
      intro: 'Digital X-ray at Bulevar umetnosti 15 — modern equipment, minimal radiation dose, image and report the same day. From 3,500 RSD.',
      priceLabel: 'from 3,500 RSD',
      keywords: 'X-ray New Belgrade, digital X-ray, chest X-ray, spine X-ray, joint X-ray, radiography Belgrade',
      indications: [
        'Chest X-ray',
        'Cervical, thoracic, lumbar spine X-ray',
        'Extremity X-ray',
        'Joint X-ray',
        'Pelvis and hip X-ray',
        'Sinus X-ray',
        'Skull X-ray',
        'Abdominal X-ray',
        'Functional spine X-ray',
      ],
      symptoms: [
        'Trauma, suspected fracture',
        'Persistent cough >3 weeks',
        'Back or neck pain',
        'Joint pain and swelling',
        'Suspected scoliosis',
        'Pre-op evaluation',
      ],
      preparation: 'No preparation required. Remove all metal (jewelry, watch, belt). Pregnant patients must notify the doctor — X-ray during pregnancy only in life-threatening indications.',
      duration: '10–15 minutes',
      report: 'Digital image and written radiologist report same day (usually within 1–2 hours).',
      body: `<h2>Digital X-ray — indications</h2>
<p>Digital X-ray uses X-rays to image bone and, less clearly, soft tissue. Digital detectors provide <strong>4–10× lower dose</strong> than film-based X-ray.</p>

<h2>Chest X-ray</h2>
<p>The most common X-ray. First-line for cough >3 weeks, dyspnea, chest pain, suspected pneumonia, tuberculosis, tumor, pleural effusion or pneumothorax.</p>

<h2>Spine X-ray</h2>
<p>Cervical, thoracic or lumbar spine. Detects degenerative changes, scoliosis, kyphosis, spondylolisthesis, fractures, tumors and congenital anomalies. Functional flexion/extension views for instability.</p>

<h3>Scoliosis in children</h3>
<p>Full-spine X-ray is the gold standard for Cobb-angle measurement. Combined with <a href="/korektivne-vezbe-za-decu-novi-beograd">corrective exercises for children</a> at PHYSIOWRLD.</p>

<h2>Joint and extremity X-ray</h2>
<p>First-line for trauma, arthritis, degenerative disease. Shoulder, elbow, wrist, hip, knee, ankle and foot.</p>

<h3>Heel spur and plantar fasciitis</h3>
<p>Lateral foot X-ray is basic for diagnosis of a calcaneal spur, often associated with plantar fasciitis. After diagnosis we recommend <strong>shockwave therapy</strong> at PHYSIOWRLD.</p>

<h2>Radiation safety</h2>
<p>Digital chest X-ray dose is ~0.02 mSv — equivalent to ~3 days of natural background radiation.</p>

<h2>When X-ray is not enough</h2>
<ul>
<li>Soft tissue (meniscus, ligaments, rotator cuff) — <strong>MRI</strong>.</li>
<li>Intracranial or pulmonary detail — <strong>CT</strong>.</li>
<li>Early osteoarthritis — <strong>MRI</strong>.</li>
</ul>`,
      pricing: [
        ['Chest X-ray', '3,500 RSD'],
        ['Spine X-ray (one region)', 'from 8,000 RSD'],
        ['Extremity X-ray', '3,500 RSD'],
        ['Joint X-ray', '3,500 RSD'],
        ['Pelvis X-ray', '3,500 RSD'],
      ],
      faqs: [
        { q: 'How much does X-ray cost?', a: 'Chest, extremity and joint X-ray 3,500 RSD. Spine X-ray from 8,000 RSD.' },
        { q: 'Is X-ray safe?', a: 'Digital X-ray uses minimal dose. Safe for all except pregnancy.' },
        { q: 'Preparation?', a: 'None; remove metal.' },
        { q: 'When is the report ready?', a: 'Same day, usually 1–2 hours.' },
        { q: 'When is MRI needed instead?', a: 'For soft tissue (meniscus, ligaments, disc hernia).' },
        { q: 'X-ray for scoliosis?', a: 'Yes — full-spine X-ray for Cobb angle.' },
        { q: 'X-ray price in New Belgrade?', a: 'Chest, extremity, joint and pelvis X-ray 3,500 RSD. Cervical spine 4,000 RSD, full-spine (for scoliosis) from 8,000 RSD.' },
        { q: 'Where to get an X-ray in New Belgrade?', a: 'Digital X-ray at the partner clinic on Bulevar umetnosti 15 (block 22, New Belgrade). From 3,500 RSD, same-day report.' },
      ],
    },
    ru: {
      title: 'Рентген Новый Белград — грудная клетка, позвоночник | от 3 500 RSD',
      metaDesc: 'Цифровой рентген в Новом Белграде — лёгкие, позвоночник, конечности, суставы. От 3 500 RSD, заключение в тот же день.',
      h1: 'Рентген Новый Белград — цифровая рентген-диагностика',
      name: 'Рентген-диагностика',
      short: 'Цифровой рентген с минимальной дозой облучения.',
      intro: 'Цифровой рентген на Bulevar umetnosti 15 — современное оборудование, минимальная доза облучения. От 3 500 RSD.',
      priceLabel: 'от 3 500 RSD',
      keywords: 'рентген Новый Белград, цифровой рентген, рентген лёгких, рентген позвоночника, рентген суставов',
      indications: ['Рентген лёгких','Рентген позвоночника','Рентген конечностей','Рентген суставов','Рентген таза','Рентген черепа'],
      symptoms: ['Травма','Кашель более 3 недель','Боль в спине','Боль в суставе'],
      preparation: 'Подготовка не требуется. Снять металлические предметы.',
      duration: '10–15 минут',
      report: 'Заключение — в тот же день.',
      body: `<h2>Цифровой рентген</h2><p>Цифровой детектор — минимальная доза облучения, быстрая обработка.</p><h2>Рентген лёгких</h2><p>Первичная диагностика при кашле, одышке, пневмонии, туберкулёзе.</p><h2>Рентген позвоночника</h2><p>Дегенеративные изменения, сколиоз, переломы. Функциональные снимки.</p><h2>Рентген суставов</h2><p>Травмы, артрит, остеоартроз. Пяточная шпора и плантарный фасциит.</p>`,
      pricing: [
        ['Рентген лёгких', '3 500 RSD'],
        ['Рентген позвоночника (одна область)', 'от 8 000 RSD'],
        ['Рентген конечности', '3 500 RSD'],
        ['Рентген сустава', '3 500 RSD'],
      ],
      faqs: [
        { q: 'Сколько стоит рентген?', a: 'Лёгкие и суставы — 3 500 RSD, позвоночник — от 8 000 RSD.' },
        { q: 'Безопасен ли?', a: 'Минимальная доза, безопасно кроме беременности.' },
        { q: 'Подготовка?', a: 'Не требуется, снять металл.' },
        { q: 'Когда заключение?', a: 'В тот же день.' },
        { q: 'Рентген цена в Новом Белграде?', a: 'Грудная клетка, суставы, конечности — 3 500 RSD; шейный отдел — 4 000; полный позвоночник — от 8 000. Bulevar umetnosti 15, заключение в тот же день.' },
      ],
    },
  },

  // ────────────────────────── KARDIOLOGIJA ──────────────────────────
  {
    slug: 'kardioloski-pregled-novi-beograd',
    anchor: 'kardiologija',
    image: '/images/diagnostika-kardiologija.jpg',
    fromPrice: '6000',
    icon: 'HeartPulse',
    sr: {
      title: 'Kardiološki pregled Novi Beograd — EKG, echo, Holter | od 6.000 RSD',
      metaDesc: 'Kardiološki pregled u Novom Beogradu — EKG, ehokardiografija (ultrazvuk srca), Holter EKG i Holter pritiska. Cena od 6.000 RSD, nalaz odmah.',
      h1: 'Kardiološki pregled Novi Beograd — kompletna kardiološka dijagnostika',
      name: 'Kardiologija',
      short: 'Kompletna kardiološka obrada — pregled, EKG, ultrazvuk srca i 24h monitoring.',
      intro: 'Kardiološki pregled kod iskusnog specijaliste kardiologa u partnerskoj ordinaciji na Bulevaru umetnosti 15. EKG, ehokardiografija, Holter EKG i 24h monitoring pritiska — sve na jednom mestu. Cena od 6.000 RSD.',
      priceLabel: 'od 6.000 RSD',
      keywords: 'kardiolog Novi Beograd, kardiološki pregled cena, EKG Novi Beograd, ultrazvuk srca, ehokardiografija cena, Holter EKG, Holter pritiska, aritmija dijagnostika',
      indications: [
        'Kardiološki pregled sa EKG-om',
        'Ultrazvuk srca (ehokardiografija)',
        'Holter EKG (24h monitoring srčanog ritma)',
        'Holter pritiska (24h monitoring krvnog pritiska)',
        'Ergometrija (test opterećenja) — po dogovoru',
        'Kompletan kardiološki pregled (paket)',
        'Preoperativna kardiološka evaluacija',
        'Kontrolni pregledi hroničnih pacijenata',
      ],
      symptoms: [
        'Bol i pritisak u grudima',
        'Palpitacije, osećaj lupanja srca',
        'Kratkoća daha pri naporu ili u miru',
        'Vrtoglavica, gubitak svesti',
        'Otok nogu, brzo zamaranje',
        'Poremećen krvni pritisak (hipertenzija)',
        'Porodična istorija srčanih bolesti',
        'Praćenje pacijenata sa infarktom, stentom, aritmijom',
      ],
      preparation: 'Za EKG i kardiološki pregled ne pijete kafu i ne pušite 2 sata pre pregleda. Ne konzumirati stimulanse. Za ehokardiografiju priprema nije potrebna. Za Holter EKG i Holter pritiska dolazak sa udobnom odećom — aparat nosite 24h tokom uobičajenih dnevnih aktivnosti.',
      duration: 'Pregled + EKG 20–30 min, ehokardiografija 25–30 min, postavljanje Holtera 15 min (nosi se 24h).',
      report: 'Nalaz nakon pregleda i ehokardiografije — odmah. Nalaz Holter EKG-a i Holter pritiska — u roku od 24–48h po vraćanju aparata.',
      body: `<h2>Šta obuhvata kardiološki pregled</h2>
<p>Kompletan kardiološki pregled u partnerskoj ordinaciji obuhvata: <strong>anamnezu, fizikalni pregled sa auskultacijom srca i pluća, merenje krvnog pritiska, palpaciju perifernih pulseva, EKG u miru</strong> i, po potrebi, dalju dijagnostiku (ehokardiografija, Holter, ergometrija). Cilj je precizna procena rizika i pravovremena dijagnoza koronarne bolesti, aritmija, valvularnih bolesti, kardiomiopatija i hipertenzije.</p>

<h2>EKG — elektrokardiogram</h2>
<p>Osnovna, najbrža i najjeftinija kardiološka dijagnostička metoda. Snimanje električne aktivnosti srca u 12 odvoda u trajanju od par minuta. Otkriva: <strong>aritmije (fibrilacija atrija, ekstrasistole, blokovi), znake ishemije, znake preležanog infarkta, hipertrofiju levog ventrikula, elektrolitne poremećaje</strong> i preduslov je za sve dalje kardiološke preglede.</p>

<h2>Ehokardiografija — ultrazvuk srca</h2>
<p>Ehokardiografija (echo) je neinvazivan ultrazvučni pregled srca koji daje detaljan uvid u <strong>strukturu i funkciju srca — veličinu srčanih šupljina, debljinu zidova, ejekcionu frakciju (EF), rad valvula, perikard i velike krvne sudove u blizini srca</strong>. Zlatni je standard u dijagnozi:</p>
<ul>
<li>Insuficijencije srca (heart failure) i procene ejekcione frakcije</li>
<li>Valvularnih bolesti — stenoze i insuficijencije aortne, mitralne, trikuspidne i pulmonalne valvule</li>
<li>Hipertenzivne bolesti srca i hipertrofične kardiomiopatije</li>
<li>Kongenitalnih anomalija (npr. bicuspid aorta, ASD, VSD)</li>
<li>Perikardijalnog izliva i perikarditisa</li>
<li>Praćenja pacijenata nakon infarkta, hemioterapije i valvularnih intervencija</li>
</ul>

<h2>Holter EKG — 24h monitoring srčanog ritma</h2>
<p>Holter EKG je uređaj koji se nosi 24 sata i kontinuirano beleži rad srca. <strong>Indikovan je kod pacijenata sa palpitacijama, sinkopama, vrtoglavicom, sumnjom na paroksizmalnu fibrilaciju atrija ili druge aritmije koje se ne vide na klasičnom EKG-u u miru.</strong> Takođe se koristi u praćenju efikasnosti antiaritmijske terapije i kod pacijenata nakon ablacije.</p>

<h2>Holter pritiska (ABPM) — 24h monitoring krvnog pritiska</h2>
<p>ABPM je zlatni standard u dijagnozi <strong>hipertenzije, "white coat" hipertenzije (samo u ordinaciji), maskirane hipertenzije, noćne hipertenzije</strong> i procene efikasnosti terapije. Aparat automatski meri pritisak svakih 15–30 minuta tokom 24 sata dana i noći. Nalaz sadrži prosečne vrednosti, cirkadijani profil i mape merenja.</p>

<h2>Kada je obavezan kardiološki pregled</h2>
<ul>
<li>Bol u grudima nepoznatog uzroka</li>
<li>Palpitacije koje se ponavljaju</li>
<li>Kratkoća daha i brzo zamaranje</li>
<li>Sinkopa (gubitak svesti)</li>
<li>Novootkrivena hipertenzija</li>
<li>Porodična istorija naglog kardiovaskularnog događaja pre 50. godine</li>
<li>Preoperativna evaluacija za velike operacije</li>
<li>Sportska medicina — pre napornih treninga i takmičenja</li>
<li>Kontrola nakon infarkta, ablacije, stenta, TAVI, protetske valvule</li>
</ul>

<h2>Nastavak nakon kardiološke evaluacije</h2>
<p>Nakon dijagnoze, kardiolog daje jasan plan: promene načina života, medikamentna terapija, dodatne intervencije. Ako je indikovana <strong>kardiološka rehabilitacija</strong> (nakon infarkta, kardiohirurške intervencije ili u okviru srčane insuficijencije), tim PHYSIOWRLD-a nastavlja rad kroz <strong>kineziterapiju, kontrolisanu ergometriju i edukaciju</strong>.</p>`,
      pricing: [
        ['Kardiološki pregled + EKG', '8.000 RSD'],
        ['Kompletan pregled (klinički + EKG + echo)', '9.000 RSD'],
        ['Ultrazvuk srca (ehokardiografija)', '6.000 RSD'],
        ['Holter EKG (24h)', '7.000 RSD'],
        ['Holter pritiska (24h)', '7.000 RSD'],
        ['Kontrolni pregled', '5.000 RSD'],
      ],
      faqs: [
        { q: 'Koliko košta kardiološki pregled?', a: 'Kardiološki pregled sa EKG-om je 8.000 RSD, ultrazvuk srca 6.000 RSD, Holter EKG i Holter pritiska po 7.000 RSD, kontrolni pregled 5.000 RSD.' },
        { q: 'Šta obuhvata ultrazvuk srca?', a: 'Procena strukture i funkcije srca, veličine šupljina, debljine zidova, ejekcione frakcije, rada valvula i perikarda. Zlatni standard u dijagnozi insuficijencije srca i valvularnih bolesti.' },
        { q: 'Šta pokazuje Holter EKG?', a: 'Kontinuirano beleži rad srca 24 sata. Otkriva paroksizmalne aritmije (npr. paroksizmalnu fibrilaciju atrija), palpitacije, blokove i procenjuje efikasnost antiaritmijske terapije.' },
        { q: 'Kada je potreban Holter pritiska?', a: 'Kod novootkrivene hipertenzije, sumnje na "white coat" hipertenziju, praćenja efikasnosti antihipertenzivne terapije i procene noćne hipertenzije.' },
        { q: 'Da li je potrebna priprema?', a: 'Za EKG bez kafe i cigareta 2 sata pre pregleda. Za ehokardiografiju priprema nije potrebna. Za Holter dolazak sa udobnom odećom.' },
        { q: 'Kada dobijam nalaz?', a: 'Pregleda i ehokardiografije — odmah. Holter EKG i Holter pritiska — 24–48h po vraćanju aparata.' },
        { q: 'Ko obavlja pregled?', a: 'Iskusan specijalista kardiolog sa dugogodišnjom kliničkom praksom.' },
        { q: 'Da li je pregled bezbolan?', a: 'Da. Sve metode (EKG, echo, Holter) su neinvazivne i potpuno bezbolne.' },
        { q: 'Kardiološki pregled cena — koliko košta u Novom Beogradu?', a: 'Kardiološki pregled cena na Novom Beogradu: prvi pregled sa EKG-om 8.000 RSD, ultrazvuk srca (ehokardiografija) 6.000 RSD, Holter EKG 24h 7.000 RSD, Holter pritiska (ABPM) 7.000 RSD, kontrolni pregled 5.000 RSD.' },
        { q: 'Koliko košta ultrazvuk srca (ehokardiografija)?', a: 'Ehokardiografija u Novom Beogradu (Bulevar umetnosti 15) košta 6.000 RSD. Uključuje procenu ejekcione frakcije, valvula, veličine šupljina i perikarda.' },
        { q: 'Koliko košta Holter EKG i Holter pritiska?', a: 'Holter EKG (24h) 7.000 RSD i Holter pritiska (ABPM) 7.000 RSD. Cena uključuje aplikaciju, aparat, analizu i pisani nalaz kardiologa.' },
        { q: 'Gde raditi kardiološki pregled u Novom Beogradu?', a: 'Kardiološki pregled radimo u partnerskoj ordinaciji na Bulevaru umetnosti 15 (Novi Beograd, blok 22) — iskusan specijalista kardiolog, cena od 6.000 RSD.' },
      ],
    },
    en: {
      title: 'Cardiology New Belgrade — ECG, echo, Holter | from 6,000 RSD',
      metaDesc: 'Cardiology consultation in New Belgrade — ECG, echocardiography, Holter ECG, ambulatory BP monitoring. From 6,000 RSD, immediate report.',
      h1: 'Cardiology New Belgrade — complete cardiac work-up',
      name: 'Cardiology',
      short: 'Full cardiac work-up — consultation, ECG, echocardiography and 24h monitoring.',
      intro: 'Cardiology consultation with an experienced cardiologist at Bulevar umetnosti 15. ECG, echocardiography, Holter ECG and 24h ambulatory BP monitoring. From 6,000 RSD.',
      priceLabel: 'from 6,000 RSD',
      keywords: 'cardiologist Belgrade, ECG, echocardiography price, Holter ECG, ambulatory BP monitor, arrhythmia diagnosis',
      indications: [
        'Cardiology consultation + ECG',
        'Echocardiography (heart ultrasound)',
        'Holter ECG (24h)',
        'Ambulatory BP monitoring (24h)',
        'Pre-op cardiac evaluation',
        'Follow-up for chronic patients',
      ],
      symptoms: [
        'Chest pain or pressure',
        'Palpitations',
        'Dyspnea on exertion or at rest',
        'Dizziness, syncope',
        'Leg swelling, fatigue',
        'Hypertension',
      ],
      preparation: 'For ECG — no coffee or cigarettes 2h before. Echocardiography — no preparation. Holter — comfortable clothes; device worn for 24h.',
      duration: 'Consult + ECG 20–30 min; echo 25–30 min; Holter setup 15 min.',
      report: 'Consult & echo — immediate. Holter — within 24–48h.',
      body: `<h2>What the cardiac work-up includes</h2>
<p>History, physical exam with cardiac and pulmonary auscultation, BP measurement, peripheral pulses, resting ECG and — as needed — further diagnostics.</p>

<h2>ECG</h2>
<p>Fastest, cheapest cardiac test. Detects arrhythmias (AFib, ectopics, blocks), ischemia, prior MI, LVH.</p>

<h2>Echocardiography</h2>
<p>Gold standard for heart failure and valvular disease. Assesses chamber sizes, wall thickness, ejection fraction, valve function and pericardium.</p>

<h2>Holter ECG</h2>
<p>24h continuous ECG. Indicated for palpitations, syncope, suspected paroxysmal AFib and other arrhythmias not seen on resting ECG.</p>

<h2>Ambulatory BP monitoring (ABPM)</h2>
<p>Gold standard for hypertension diagnosis, "white coat" hypertension, masked hypertension and nocturnal hypertension.</p>

<h2>Follow-up rehabilitation</h2>
<p>After MI, cardiac surgery or heart failure — cardiac rehabilitation at PHYSIOWRLD.</p>`,
      pricing: [
        ['Consultation + ECG', '8,000 RSD'],
        ['Complete work-up', '9,000 RSD'],
        ['Echocardiography', '6,000 RSD'],
        ['Holter ECG', '7,000 RSD'],
        ['Ambulatory BP', '7,000 RSD'],
      ],
      faqs: [
        { q: 'How much does the cardiac exam cost?', a: 'Consult + ECG 8,000 RSD; echo 6,000; Holter ECG/BP 7,000 each.' },
        { q: 'What does echocardiography show?', a: 'Chamber size, wall thickness, EF, valve function, pericardium.' },
        { q: 'What is Holter ECG?', a: '24h continuous ECG for paroxysmal arrhythmias.' },
        { q: 'When is ABPM needed?', a: 'New hypertension, white-coat, therapy titration.' },
        { q: 'Preparation?', a: 'No coffee/cigarettes 2h before ECG.' },
        { q: 'Report timing?', a: 'Immediate for consult/echo; 24–48h for Holter.' },
        { q: 'Cardiology price in New Belgrade?', a: 'First consult + ECG 8,000 RSD, echocardiography 6,000 RSD, Holter ECG 7,000 RSD, ABPM 7,000 RSD, follow-up 5,000 RSD — Bulevar umetnosti 15.' },
      ],
    },
    ru: {
      title: 'Кардиолог Новый Белград — ЭКГ, эхо, холтер | от 6 000 RSD',
      metaDesc: 'Консультация кардиолога — ЭКГ, эхокардиография, холтер ЭКГ и АД. От 6 000 RSD, заключение сразу.',
      h1: 'Кардиологический приём Новый Белград',
      name: 'Кардиология',
      short: 'Полное кардиологическое обследование.',
      intro: 'Приём у опытного кардиолога — ЭКГ, эхо, холтер. От 6 000 RSD.',
      priceLabel: 'от 6 000 RSD',
      keywords: 'кардиолог Новый Белград, ЭКГ, эхокардиография, холтер',
      indications: ['Консультация + ЭКГ','Эхокардиография','Холтер ЭКГ','Холтер АД'],
      symptoms: ['Боль в груди','Учащённое сердцебиение','Одышка','Гипертензия'],
      preparation: 'Без кофе и сигарет 2 часа до ЭКГ.',
      duration: '20–30 минут',
      report: 'ЭКГ и эхо — сразу; холтер — 24–48 ч.',
      body: `<h2>Кардиологический приём</h2><p>Анамнез, физикальный осмотр, ЭКГ.</p><h2>Эхокардиография</h2><p>Золотой стандарт при сердечной недостаточности и клапанной патологии.</p><h2>Холтер ЭКГ</h2><p>24 часа непрерывной записи для выявления пароксизмальных аритмий.</p><h2>Холтер АД</h2><p>Диагностика гипертензии и оценка терапии.</p>`,
      pricing: [
        ['Консультация + ЭКГ', '8 000 RSD'],
        ['Эхокардиография', '6 000 RSD'],
        ['Холтер ЭКГ', '7 000 RSD'],
        ['Холтер АД', '7 000 RSD'],
      ],
      faqs: [
        { q: 'Сколько стоит?', a: 'Консультация + ЭКГ — 8 000, эхо — 6 000.' },
        { q: 'Что показывает эхо?', a: 'Структуру и функцию сердца, фракцию выброса.' },
        { q: 'Что такое холтер ЭКГ?', a: '24-часовая запись для выявления аритмий.' },
        { q: 'Подготовка?', a: 'Без кофе за 2 часа.' },
        { q: 'Когда заключение?', a: 'Сразу, холтер — 24–48 ч.' },
        { q: 'Кардиолог цена в Новом Белграде?', a: 'Первичная + ЭКГ — 8 000 RSD, эхо — 6 000, холтер ЭКГ/АД — 7 000, контрольная — 5 000. Bulevar umetnosti 15.' },
      ],
    },
  },

  // ────────────────────────── LABORATORIJA ──────────────────────────
  {
    slug: 'laboratorija-novi-beograd',
    anchor: 'laboratorija',
    image: '/images/diagnostika-laboratorija.jpg',
    fromPrice: '500',
    icon: 'FlaskConical',
    sr: {
      title: 'Laboratorija Novi Beograd — krv, hormoni, tumor markeri | brzi nalazi',
      metaDesc: 'Laboratorijske analize u Novom Beogradu — krvna slika, biohemija, hormoni, tumor markeri, mikrobiologija. Bulevar umetnosti 15. Rezultati u roku od nekoliko sati do 24h.',
      h1: 'Laboratorija Novi Beograd — kompletne laboratorijske analize',
      name: 'Laboratorijske analize',
      short: 'Više stotina analiza — od kompletne krvne slike do hormona i tumor markera.',
      intro: 'Laboratorijske analize u partnerskoj ordinaciji na Bulevaru umetnosti 15. Vađenje krvi ujutro natašte, rezultati u roku od nekoliko sati do 24h za većinu panela. Cene po analizi (cenovnik na uvid).',
      priceLabel: 'po analizi',
      keywords: 'laboratorija Novi Beograd, laboratorijske analize cena, krvna slika, biohemija, hormoni, tumor markeri, TSH, PSA, D-dimer, CRP, HbA1c',
      indications: [
        'Kompletna krvna slika (KKS) sa leukocitarnom formulom',
        'Biohemija (glukoza, urea, kreatinin, ALT, AST, bilirubin, holesterol, trigliceridi, HDL, LDL)',
        'Hormoni (TSH, FT3, FT4, kortizol, testosteron, estradiol, prolaktin, HCG)',
        'Tumor markeri (PSA, CEA, CA 125, CA 15-3, CA 19-9, AFP)',
        'Koagulacija (PT, aPTT, INR, D-dimer, fibrinogen)',
        'Vitamin D, vitamin B12, feritin, gvožđe',
        'Diabetes panel (glukoza natašte, HbA1c, OGTT, insulin, C-peptid)',
        'Urin — kompletan pregled i urin kultura',
        'Mikrobiologija, mikologija, parazitologija',
        'Serologija (hepatitis B, C, HIV, sifilis)',
      ],
      symptoms: [
        'Umor, malaksalost, gubitak težine',
        'Palpitacije, znojenje, promene raspoloženja (sumnja na hormonske poremećaje)',
        'Povišene vrednosti pritiska ili šećera',
        'Bol u grudima, otok nogu (D-dimer)',
        'Infekcije, groznica, pojačan CRP',
        'Preventivna sistematska analiza',
        'Trudničke analize',
        'Preoperativne pripreme',
      ],
      preparation: 'Za većinu analiza — vađenje krvi ujutro natašte, 10–12 sati bez hrane (voda dozvoljena). Za lipidni profil obavezno natašte 12 sati. Za hormone štitne žlezde uzimati terapiju kako je predviđeno. Za PSA izbegavati polni odnos 48h pre vađenja. Ne pušiti 2 sata pre vađenja.',
      duration: 'Vađenje krvi 5 minuta.',
      report: 'Većina rezultata u roku od nekoliko sati; hormoni i tumor markeri 24h; specifični mikrobiološki testovi 48–72h.',
      body: `<h2>Laboratorijske analize — panel po panel</h2>
<p>Laboratorija u partnerskoj ordinaciji obuhvata više stotina pojedinačnih analiza — od svakodnevnih (KKS, biohemija) do specifičnih (hormoni, tumor markeri, serologija, mikrobiologija). Vađenje krvi obavlja iskusna medicinska sestra, a rezultati se izdaju u kratkim rokovima.</p>

<h2>Kompletna krvna slika (KKS)</h2>
<p>Osnovna analiza koja se radi u sklopu skoro svakog sistematskog pregleda. Uključuje: <strong>eritrocite (RBC), hemoglobin (Hgb), hematokrit (Hct), MCV, MCH, MCHC, RDW, leukocite (WBC) sa formulom, trombocite (PLT)</strong>. Otkriva anemije, infekcije, inflamacije, trombocitopenije, leukemije i mnoge druge poremećaje.</p>

<h2>Biohemijske analize</h2>
<p>Standardni biohemijski panel: <strong>glukoza, urea, kreatinin (procena bubrežne funkcije), ALT/AST (jetreni enzimi), bilirubin, holesterol (ukupan, HDL, LDL, trigliceridi), elektroliti (Na, K, Cl)</strong>. Prošireni panel dodaje amilazu, lipazu, alkalnu fosfatazu (ALP), gama-GT (GGT), LDH, CK, urate.</p>

<h3>Diabetes panel</h3>
<p>Za dijagnozu i praćenje dijabetesa: <strong>glukoza natašte, oralni test tolerancije glukoze (OGTT), glikozilirani hemoglobin (HbA1c) — proseka šećera za poslednja 3 meseca, insulin i C-peptid</strong>.</p>

<h2>Hormonski panel</h2>
<p><strong>Štitna žlezda</strong> — TSH, FT3, FT4, anti-TPO, anti-TG (Hashimoto, Graves, hipotireoza, hipertireoza).</p>
<p><strong>Reproduktivni hormoni</strong> — testosteron, estradiol, progesteron, LH, FSH, prolaktin, DHEA-S. Ključni u dijagnostici PCOS, neplodnosti, hirsutizma, menopauze.</p>
<p><strong>Adrenalni</strong> — kortizol ujutro i uveče, ACTH, aldosteron.</p>
<p><strong>Trudnička analiza</strong> — β-hCG.</p>

<h2>Tumor markeri</h2>
<p>Koriste se za skrining i praćenje: <strong>PSA (prostata), CA 125 (jajnici), CA 15-3 (dojka), CA 19-9 (pankreas), CEA (kolon), AFP (jetra), HE4</strong>. Nikada nisu samostalno dijagnostički — uvek se tumače u kliničkom kontekstu.</p>

<h2>Kardiovaskularni i inflamatorni markeri</h2>
<p><strong>Troponin I/T</strong> — dijagnostika akutnog infarkta miokarda.<br />
<strong>D-dimer</strong> — isključivanje duboke venske tromboze i plućne embolije.<br />
<strong>CRP i sedimentacija (SE)</strong> — nespecifični markeri inflamacije.<br />
<strong>Prokalcitonin</strong> — razlikovanje bakterijskih od virusnih infekcija.</p>

<h2>Vitamini i minerali</h2>
<p><strong>Vitamin D (25-OH)</strong> — deficit je izuzetno čest, povezan sa bolovima u leđima, umoru, imunološkim poremećajima.<br />
<strong>Vitamin B12 i folna kiselina</strong> — kod anemije, neuropatija.<br />
<strong>Gvožđe, feritin, TIBC</strong> — potvrda ili isključivanje deficita gvožđa.</p>

<h2>Preoperativne i preventivne analize</h2>
<p>Standardni preoperativni panel: <strong>KKS, biohemija, koagulacija, glukoza, krvna grupa i Rh, serologija HIV/HBV/HCV</strong>. Preventivni sistematski pregled preporučuje se jednom godišnje za odrasle i sadrži KKS, biohemiju, lipidni profil, HbA1c, TSH i, po polu, PSA ili tumor markere.</p>`,
      pricing: [
        ['Kompletna krvna slika (KKS)', 'po cenovniku'],
        ['Biohemija (osnovni panel)', 'po cenovniku'],
        ['Hormoni štitne žlezde (TSH, FT3, FT4)', 'po cenovniku'],
        ['PSA', 'po cenovniku'],
        ['CA 125 / CA 15-3 / CA 19-9', 'po cenovniku'],
        ['Vitamin D (25-OH)', 'po cenovniku'],
        ['HbA1c', 'po cenovniku'],
        ['D-dimer', 'po cenovniku'],
      ],
      faqs: [
        { q: 'Da li je potrebna priprema za analize krvi?', a: 'Za većinu analiza — vađenje ujutro natašte, 10–12 sati bez hrane. Voda dozvoljena. Za lipidni profil obavezno natašte 12h. Za PSA izbegavati polni odnos 48h.' },
        { q: 'Kada dobijam rezultate?', a: 'Za standardne analize (KKS, biohemija) u roku od nekoliko sati. Hormoni, tumor markeri i specifične analize 24h. Mikrobiološki testovi 48–72h.' },
        { q: 'Da li se rade hormoni štitne žlezde?', a: 'Da. Kompletan panel TSH, FT3, FT4, anti-TPO, anti-TG za dijagnostiku Hashimoto tiroiditisa, Graves-Basedowa i drugih poremećaja.' },
        { q: 'Da li se rade tumor markeri?', a: 'Da. PSA, CA 125, CA 15-3, CA 19-9, CEA, AFP, HE4. Uvek se tumače u kliničkom kontekstu, nikad izolovano.' },
        { q: 'Da li je potrebno uput?', a: 'Nije. Analize možete zakazati direktno.' },
        { q: 'Da li mogu doći bez zakazivanja?', a: 'Preporučujemo zakazivanje termina radi kratkog čekanja.' },
        { q: 'Da li se radi analiza urina?', a: 'Da. Kompletan pregled urina, urin kultura i sedimentski pregled. Uzorak donosite u sterilnoj posudici, po mogućnosti prvi jutarnji urin.' },
        { q: 'Da li se radi trudnički test (β-hCG)?', a: 'Da. Kvantitativni β-hCG iz krvi je najosetljiviji test za trudnoću i za praćenje toka trudnoće.' },
        { q: 'Laboratorija cena — koliko koštaju analize krvi u Novom Beogradu?', a: 'Cene analiza krvi u Novom Beogradu (Bulevar umetnosti 15): KKS oko 700 RSD, biohemijski paket (glukoza, holesterol, HDL, LDL, trigliceridi, urea, kreatinin, transaminaze) od 2.500 RSD, TSH oko 1.200 RSD, kompletan štitna panel 4.500 RSD, PSA 1.500 RSD, D-dimer 2.500 RSD, vitamin D 2.500 RSD.' },
        { q: 'Koliko košta vađenje krvi?', a: 'Vađenje krvi (uzorkovanje) je uključeno u cenu analize — nema dodatnih troškova.' },
        { q: 'Gde raditi analize krvi u Novom Beogradu?', a: 'Laboratorijske analize radimo na Bulevaru umetnosti 15 (blok 22, Novi Beograd) — bez zakazivanja, kratko čekanje, rezultati u roku od nekoliko sati do 24h.' },
      ],
    },
    en: {
      title: 'Laboratory New Belgrade — blood, hormones, tumor markers | fast results',
      metaDesc: 'Laboratory analyses in New Belgrade — CBC, biochemistry, hormones, tumor markers, microbiology. Bulevar umetnosti 15. Results from a few hours to 24h.',
      h1: 'Laboratory New Belgrade — complete lab analyses',
      name: 'Laboratory analyses',
      short: 'Hundreds of tests — from CBC to hormones and tumor markers.',
      intro: 'Laboratory at Bulevar umetnosti 15. Morning fasting draw, results within a few hours to 24h for most panels.',
      priceLabel: 'per test',
      keywords: 'laboratory Belgrade, blood tests, CBC, biochemistry, hormones, tumor markers, TSH, PSA, HbA1c, D-dimer',
      indications: [
        'Complete blood count (CBC)',
        'Biochemistry panel',
        'Thyroid hormones (TSH, FT3, FT4)',
        'Reproductive hormones',
        'Tumor markers (PSA, CA 125, CA 15-3, CA 19-9, CEA, AFP)',
        'Coagulation (PT, aPTT, INR, D-dimer)',
        'Diabetes panel (glucose, HbA1c, OGTT)',
        'Vitamin D, B12, ferritin, iron',
        'Urinalysis and urine culture',
        'Serology (HBV, HCV, HIV, syphilis)',
      ],
      symptoms: [
        'Fatigue, weight loss',
        'Palpitations, sweating, mood changes',
        'Elevated BP or glucose',
        'Chest pain, leg swelling',
        'Infection, fever',
        'Preventive check-up',
      ],
      preparation: 'Morning fasting draw (10–12h). Lipid panel — 12h fasting. Avoid coffee/cigarettes 2h before. PSA — no intercourse 48h before.',
      duration: 'Blood draw 5 minutes.',
      report: 'Most results within a few hours; hormones and tumor markers 24h; microbiology 48–72h.',
      body: `<h2>Laboratory analyses — panel by panel</h2>
<p>Hundreds of individual tests from routine to specialized. Experienced phlebotomy, rapid turnaround.</p>

<h2>Complete blood count (CBC)</h2>
<p>Basic screening: RBC, Hgb, Hct, MCV, WBC with differential, platelets. Detects anemias, infections, inflammation.</p>

<h2>Biochemistry</h2>
<p>Glucose, urea, creatinine, ALT/AST, bilirubin, lipid panel, electrolytes. Extended: amylase, lipase, GGT, LDH.</p>

<h2>Hormones</h2>
<p>Thyroid — TSH, FT3, FT4, anti-TPO. Reproductive — testosterone, estradiol, LH, FSH, prolactin. Adrenal — cortisol, ACTH.</p>

<h2>Tumor markers</h2>
<p>PSA, CA 125, CA 15-3, CA 19-9, CEA, AFP, HE4 — always interpreted in clinical context.</p>

<h2>Cardiovascular / inflammatory</h2>
<p>Troponin, D-dimer, CRP, procalcitonin.</p>

<h2>Vitamins and minerals</h2>
<p>Vitamin D, B12, ferritin, iron.</p>`,
      pricing: [
        ['Complete blood count', 'per price list'],
        ['Biochemistry panel', 'per price list'],
        ['Thyroid hormones', 'per price list'],
        ['PSA', 'per price list'],
        ['Tumor markers', 'per price list'],
      ],
      faqs: [
        { q: 'Preparation for blood tests?', a: 'Morning fasting 10–12h. Water allowed. No coffee 2h before.' },
        { q: 'When are results ready?', a: 'CBC/biochemistry hours; hormones/tumor markers 24h; microbiology 48–72h.' },
        { q: 'Thyroid hormones available?', a: 'Yes — full TSH, FT3, FT4, anti-TPO panel.' },
        { q: 'Tumor markers?', a: 'Yes — PSA, CA 125, CA 15-3, CA 19-9, CEA, AFP.' },
        { q: 'Referral needed?', a: 'No.' },
        { q: 'Urinalysis?', a: 'Yes — complete urinalysis and culture.' },
        { q: 'Lab price in New Belgrade?', a: 'CBC ~700 RSD, full biochemistry from 2,500 RSD, TSH ~1,200, full thyroid panel 4,500, PSA 1,500, vitamin D 2,500, D-dimer 2,500 — Bulevar umetnosti 15.' },
      ],
    },
    ru: {
      title: 'Лаборатория Новый Белград — кровь, гормоны, онкомаркеры',
      metaDesc: 'Лабораторные анализы в Новом Белграде — общий анализ крови, биохимия, гормоны, онкомаркеры. Результаты от нескольких часов до 24 ч.',
      h1: 'Лаборатория Новый Белград',
      name: 'Лабораторные анализы',
      short: 'Сотни анализов — от общего анализа крови до онкомаркеров.',
      intro: 'Лаборатория на Bulevar umetnosti 15. Забор утром натощак, результаты за несколько часов до 24 ч.',
      priceLabel: 'за анализ',
      keywords: 'лаборатория Новый Белград, анализ крови, биохимия, гормоны, онкомаркеры',
      indications: ['Общий анализ крови','Биохимия','Гормоны щитовидной железы','Онкомаркеры','Коагулограмма','Витамин D, B12'],
      symptoms: ['Слабость','Учащённое сердцебиение','Повышенное давление','Инфекции','Профилактика'],
      preparation: 'Утром натощак 10–12 ч. Липидный профиль — строго натощак 12 ч.',
      duration: '5 минут',
      report: 'Большинство — за несколько часов; гормоны — 24 ч.',
      body: `<h2>Лабораторные анализы</h2><p>Сотни анализов — от общего анализа крови до онкомаркеров.</p><h2>Общий анализ крови</h2><p>Эритроциты, гемоглобин, лейкоциты, тромбоциты.</p><h2>Биохимия</h2><p>Глюкоза, креатинин, АЛТ/АСТ, липиды.</p><h2>Гормоны</h2><p>ТТГ, Т3, Т4, тестостерон, эстрадиол.</p><h2>Онкомаркеры</h2><p>ПСА, СА 125, СА 15-3, СА 19-9.</p>`,
      pricing: [
        ['Общий анализ крови', 'по прайсу'],
        ['Биохимия', 'по прайсу'],
        ['Гормоны', 'по прайсу'],
        ['Онкомаркеры', 'по прайсу'],
      ],
      faqs: [
        { q: 'Подготовка?', a: 'Натощак 10–12 часов.' },
        { q: 'Когда результаты?', a: 'От нескольких часов до 24 ч.' },
        { q: 'Гормоны щитовидки?', a: 'Да — полный панель.' },
        { q: 'Онкомаркеры?', a: 'Да — ПСА, СА 125, СА 15-3, СА 19-9, CEA, AFP.' },
        { q: 'Направление?', a: 'Не требуется.' },
        { q: 'Анализы крови цена в Новом Белграде?', a: 'ОАК ~700 RSD, биохимия от 2 500, ТТГ ~1 200, полный панель щитовидной 4 500, ПСА 1 500, витамин D 2 500. Bulevar umetnosti 15.' },
      ],
    },
  },

  // ────────────────────────── SPECIJALISTIČKI PREGLEDI ──────────────────────────
  {
    slug: 'specijalisticki-pregledi-novi-beograd',
    anchor: 'specijalisti',
    image: '/images/diagnostika-specijalisticki.jpg',
    fromPrice: '5000',
    icon: 'Stethoscope',
    sr: {
      title: 'Specijalistički pregledi Novi Beograd — internista, ortoped, dermatolog | od 5.000 RSD',
      metaDesc: 'Specijalistički pregledi u Novom Beogradu — internista, kardiolog, gastroenterolog, dermatolog, ortoped, urolog, neuropsihijatar, klinički psiholog. Cena od 5.000 RSD.',
      h1: 'Specijalistički pregledi Novi Beograd — konsultacije renomiranih specijalista',
      name: 'Specijalistički pregledi',
      short: 'Pregledi renomiranih specijalista različitih grana medicine.',
      intro: 'Konsultacije specijalista različitih grana medicine u partnerskoj ordinaciji na Bulevaru umetnosti 15 — internista, kardiolog, gastroenterolog, dermatolog, ortoped, urolog, neuropsihijatar, klinički psiholog i drugi. Cena od 5.000 RSD.',
      priceLabel: 'od 5.000 RSD',
      keywords: 'specijalistički pregled Novi Beograd, internista, ortoped Novi Beograd, dermatolog cena, gastroenterolog, urolog, neuropsihijatar, klinički psiholog',
      indications: [
        'Internista',
        'Kardiolog',
        'Gastroenterolog',
        'Dermatolog',
        'Ortoped',
        'ORL (otorinolaringolog)',
        'Neuropsihijatar',
        'Klinički psiholog',
        'Urolog',
        'Endokrinolog',
        'Pulmolog',
        'Reumatolog',
      ],
      symptoms: [
        'Nespecifičan bol i simptomi koji zahtevaju širu obradu',
        'Bol u zglobovima, kičmi, mišićima (ortoped, reumatolog)',
        'Digestivne tegobe (gastroenterolog)',
        'Kožne promene i mladeži (dermatolog)',
        'Urinarni simptomi (urolog)',
        'Poremećaji spavanja, anksioznost, depresija (neuropsihijatar, psiholog)',
        'Hormonski poremećaji (endokrinolog)',
      ],
      preparation: 'Zavisi od specijalnosti — najčešće nije potrebna posebna priprema. Ponesite sa sobom sve dosadašnje nalaze, listu lekova koje uzimate i, po potrebi, dnevnik simptoma.',
      duration: '20–30 minuta.',
      report: 'Pisani nalaz sa preporukama, dijagnozom i terapijskim planom dobijate odmah nakon pregleda.',
      body: `<h2>Ko su naši specijalisti</h2>
<p>Partnerska dijagnostička ordinacija na Bulevaru umetnosti 15 okuplja iskusne specijaliste sa dugogodišnjom kliničkom praksom u velikim beogradskim zdravstvenim ustanovama i, delom, sa iskustvom rada u inostranstvu. Svaki pregled obuhvata: <strong>detaljnu anamnezu, fizikalni pregled, tumačenje eventualnih nalaza, dijagnozu i terapijski plan sa pisanim izveštajem</strong>.</p>

<h2>Internista</h2>
<p>Internistički pregled je "prva linija" za nespecifične tegobe: <strong>hronični umor, glavobolje, poremećaji krvnog pritiska, digestivne tegobe, gubitak težine, palpitacije, sistematski pregled</strong>. Internista sagledava pacijenta u celini i po potrebi upućuje na užeg specijalistu.</p>

<h2>Ortoped</h2>
<p>Ortopedski pregled je osnovna dijagnostika mišićno-skeletnih problema: <strong>bol u ramenu, kolenu, kuku, stopalu, kičmi; sportske povrede; artritisi; deformiteti stopala i kičme (hallux valgus, skolioza)</strong>. Nakon pregleda i po potrebi RTG/MR nalaza, ortoped daje konzervativni plan (fizikalna terapija, ortoze, injekcije) ili preporučuje hirurški tretman.</p>

<h3>Nastavak u PHYSIOWRLD-u</h3>
<p>Nakon ortopedske dijagnoze, tim PHYSIOWRLD-a nastavlja konzervativni tretman kroz <strong>fizikalnu terapiju, kineziterapiju, shockwave, lasersku terapiju, Active Release, kiropraktiku</strong>. Za pripremu i oporavak od operacije nudimo dedikovane programe (<a href="/priprema-za-operaciju-kolena-novi-beograd">koleno</a>, <a href="/priprema-za-operaciju-kuka-novi-beograd">kuk</a>, <a href="/rame-postoperativna-rehabilitacija-novi-beograd">rame</a>, <a href="/artroskopija-postoperativna-rehabilitacija-novi-beograd">artroskopija</a>).</p>

<h2>Dermatolog</h2>
<p>Pregled kože, sluzokoža, kose i noktiju. Uključuje <strong>dermoskopiju mladeža (skrining melanoma), tretman akni, ekcema, psorijaze, rozacee, hroničnih dermatitisa, gljivičnih infekcija i alopecije</strong>. Preporučuje se jednom godišnje kod svakoga sa više od 20 mladeža ili porodičnom istorijom melanoma.</p>

<h2>Gastroenterolog</h2>
<p>Konsultacija za: <strong>hronične digestivne tegobe, refluksnu bolest (GERB), gastritis, ulkus, sindrom iritabilnog creva (IBS), Crohnovu bolest, ulcerozni kolitis, hronične hepatopatije, holelitijazu</strong>. Po potrebi indikuje gastroskopiju i kolonoskopiju.</p>

<h2>Urolog</h2>
<p>Pregled kod muškaraca i žena za: <strong>kamen u bubregu, urinarnu infekciju, bolove u lumbalnom predelu, hronični prostatitis, benignu hiperplaziju prostate (BPH), poremećaje mokrenja, mušku neplodnost i varikocelu</strong>. Kombinuje se sa ultrazvukom i doplerom.</p>

<h2>Neuropsihijatar i klinički psiholog</h2>
<p>Neuropsihijatar tretira <strong>anksiozne poremećaje, depresiju, panične napade, poremećaje spavanja, hronične glavobolje i migrenu</strong>. Klinički psiholog nudi psihoterapiju i psihodijagnostiku (kognitivno-bihevioralna terapija, psihotestiranje).</p>

<h2>ORL — otorinolaringolog</h2>
<p>Pregled uva, nosa i grla: <strong>hronične sinusitise, tinitus (zujanje u ušima), vrtoglavice, poremećaje sluha, alergijske rinitise, otitise, promuklost i hronične laringitise</strong>. Uz otoskopiju i, po potrebi, audiometriju.</p>

<h2>Endokrinolog</h2>
<p>Konsultacija za: <strong>bolesti štitne žlezde (Hashimoto, Graves, čvorovi), dijabetes tip 2, gojaznost, osteoporozu, PCOS, insulinsku rezistenciju, poremećaje reproduktivnih hormona i menopauzu</strong>. Uz laboratorijske analize i ultrazvuk štitne žlezde.</p>`,
      pricing: [
        ['Specijalistički pregled (prvi)', '7.000 RSD'],
        ['Kontrolni pregled', '5.000 RSD'],
        ['Dermoskopski pregled mladeža', 'od 5.000 RSD'],
        ['Neuropsihijatrijski / psihološki pregled', 'od 7.000 RSD'],
      ],
      faqs: [
        { q: 'Koliko košta specijalistički pregled?', a: 'Prvi specijalistički pregled košta 7.000 RSD, kontrolni pregled 5.000 RSD. Za specifične konsultacije (dermoskopija, psihodijagnostika) cene se razlikuju.' },
        { q: 'Kojim specijalistima mogu da odem?', a: 'Internista, kardiolog, gastroenterolog, dermatolog, ortoped, ORL, neuropsihijatar, klinički psiholog, urolog, endokrinolog, pulmolog, reumatolog.' },
        { q: 'Da li je potrebno uput?', a: 'Nije potrebno uput. Pregled zakazujete direktno.' },
        { q: 'Da li je potrebna priprema?', a: 'Najčešće ne. Ponesite dosadašnje nalaze i listu lekova.' },
        { q: 'Koliko traje pregled?', a: '20–30 minuta.' },
        { q: 'Šta obuhvata pregled?', a: 'Anamnezu, fizikalni pregled, tumačenje nalaza, dijagnozu, terapijski plan i pisani izveštaj.' },
        { q: 'Da li se pregleda i deca?', a: 'Zavisi od specijaliste — kod pedijatrijskih pitanja preporučuje se pedijatar; ortoped i dermatolog pregledaju i decu i odrasle.' },
        { q: 'Da li mogu direktno na dopunske preglede?', a: 'Da — nakon pregleda, specijalista može istog dana ili u kratkom roku indikovati laboratoriju, ultrazvuk, RTG ili konsultaciju drugog specijaliste.' },
        { q: 'Specijalistički pregled cena — koliko košta u Novom Beogradu?', a: 'Cena specijalističkog pregleda na Novom Beogradu: prvi pregled 7.000 RSD, kontrolni pregled 5.000 RSD. Kod svih specijalista — internista, ortoped, dermatolog, endokrinolog, gastroenterolog, neuropsihijatar, urolog, ORL, pulmolog, reumatolog.' },
        { q: 'Koliko košta pregled ortopeda u Novom Beogradu?', a: 'Ortopedski pregled na Novom Beogradu (Bulevar umetnosti 15) košta 7.000 RSD i uključuje kliničku procenu, tumačenje RTG/UZ nalaza i terapijski plan.' },
        { q: 'Koliko košta pregled dermatologa (dermoskopija)?', a: 'Dermatološki pregled 7.000 RSD; dermoskopija mladeža po pregledu 500 RSD. Preporučuje se jednom godišnje kao skrining melanoma.' },
        { q: 'Gde ići kod specijaliste u Novom Beogradu?', a: 'Specijalističke preglede zakazujemo u partnerskoj ordinaciji na Bulevaru umetnosti 15 (Novi Beograd, blok 22). Bez uputa, termin u roku od 24–72h, prvi pregled 7.000 RSD.' },
      ],
    },
    en: {
      title: 'Specialist consultations New Belgrade — internal, orthopedics | from 5,000 RSD',
      metaDesc: 'Specialist consultations in New Belgrade — internal medicine, cardiology, gastroenterology, dermatology, orthopedics, urology, ORL, neuropsychiatry. From 5,000 RSD.',
      h1: 'Specialist consultations New Belgrade',
      name: 'Specialist consultations',
      short: 'Consultations with recognized specialists across disciplines.',
      intro: 'Consultations across specialties at Bulevar umetnosti 15 — internal medicine, cardiology, gastroenterology, dermatology, orthopedics, urology, ORL, neuropsychiatry, clinical psychology. From 5,000 RSD.',
      priceLabel: 'from 5,000 RSD',
      keywords: 'specialist consultation Belgrade, internist, orthopedist, dermatologist, gastroenterologist, urologist, neuropsychiatrist',
      indications: [
        'Internal medicine','Cardiology','Gastroenterology','Dermatology','Orthopedics','ORL','Neuropsychiatry','Clinical psychology','Urology','Endocrinology','Pulmonology','Rheumatology'
      ],
      symptoms: [
        'Non-specific pain requiring broader work-up',
        'Joint, spine, muscle pain (orthopedist)',
        'Digestive issues (gastroenterologist)',
        'Skin changes (dermatologist)',
        'Urinary symptoms (urologist)',
        'Sleep, anxiety, depression',
        'Hormonal disorders',
      ],
      preparation: 'Usually none. Bring prior imaging, lab results and medication list.',
      duration: '20–30 minutes.',
      report: 'Written report with diagnosis and treatment plan immediately after the visit.',
      body: `<h2>Our specialists</h2>
<p>Experienced clinicians from major Belgrade hospitals, some with international experience. Every consultation covers detailed history, examination, interpretation of imaging/lab, diagnosis, treatment plan and written report.</p>

<h2>Internal medicine</h2>
<p>First-line for non-specific complaints: chronic fatigue, headaches, blood pressure disorders, digestive issues, weight loss.</p>

<h2>Orthopedics</h2>
<p>Musculoskeletal issues — shoulder, knee, hip, spine, sports injuries, arthritis. Follows up with physiotherapy at PHYSIOWRLD.</p>

<h2>Dermatology</h2>
<p>Skin, mucosa, hair, nails; dermoscopy (melanoma screening); acne, eczema, psoriasis.</p>

<h2>Gastroenterology</h2>
<p>GERD, gastritis, IBS, Crohn, ulcerative colitis, hepatitis, cholelithiasis.</p>

<h2>Urology</h2>
<p>Renal stones, UTI, chronic prostatitis, BPH, male infertility, varicocele.</p>

<h2>Neuropsychiatry & psychology</h2>
<p>Anxiety, depression, panic disorders, sleep disorders, migraine, CBT, psychodiagnostics.</p>`,
      pricing: [
        ['First specialist visit', '7,000 RSD'],
        ['Follow-up', '5,000 RSD'],
        ['Dermoscopy', 'from 5,000 RSD'],
        ['Neuropsychiatry', 'from 7,000 RSD'],
      ],
      faqs: [
        { q: 'How much?', a: 'First visit 7,000 RSD, follow-up 5,000 RSD.' },
        { q: 'Which specialists?', a: 'Internal, cardiology, gastro, derm, ortho, ORL, neuropsych, psychology, urology, endo, pulmo, rheum.' },
        { q: 'Referral?', a: 'Not required.' },
        { q: 'Preparation?', a: 'None; bring prior records.' },
        { q: 'Duration?', a: '20–30 min.' },
        { q: 'Kids?', a: 'Depends on specialty; orthopedist and dermatologist see both children and adults.' },
        { q: 'Specialist visit price in New Belgrade?', a: 'First specialist visit 7,000 RSD, follow-up 5,000 RSD — internal medicine, orthopedics, dermatology, cardiology, endocrinology, gastro, urology, ORL, neuropsych, pulmonology, rheumatology. Bulevar umetnosti 15.' },
      ],
    },
    ru: {
      title: 'Консультации специалистов Новый Белград — от 5 000 RSD',
      metaDesc: 'Консультации специалистов — терапевт, ортопед, дерматолог, гастроэнтеролог, уролог, ЛОР, невропсихиатр. От 5 000 RSD.',
      h1: 'Консультации специалистов Новый Белград',
      name: 'Консультации специалистов',
      short: 'Консультации ведущих специалистов.',
      intro: 'Консультации специалистов в партнёрской клинике на Bulevar umetnosti 15. От 5 000 RSD.',
      priceLabel: 'от 5 000 RSD',
      keywords: 'консультация специалиста Новый Белград, терапевт, ортопед, дерматолог, уролог',
      indications: ['Терапевт','Кардиолог','Гастроэнтеролог','Дерматолог','Ортопед','ЛОР','Невропсихиатр','Психолог','Уролог','Эндокринолог'],
      symptoms: ['Неспецифические жалобы','Боль в суставах','Проблемы с ЖКТ','Изменения кожи','Урологические симптомы'],
      preparation: 'Подготовка не требуется. Возьмите с собой предыдущие результаты.',
      duration: '20–30 минут',
      report: 'Письменное заключение — сразу.',
      body: `<h2>Наши специалисты</h2><p>Опытные врачи с многолетней практикой. Каждая консультация — анамнез, осмотр, интерпретация результатов, диагноз, план лечения, письменное заключение.</p><h2>Терапевт</h2><p>Первичное обследование при неспецифических жалобах.</p><h2>Ортопед</h2><p>Боль в плече, колене, тазобедренном, позвоночнике. Продолжение реабилитации в PHYSIOWRLD.</p><h2>Дерматолог</h2><p>Кожа, дерматоскопия родинок.</p>`,
      pricing: [
        ['Первичная консультация', '7 000 RSD'],
        ['Контрольный приём', '5 000 RSD'],
      ],
      faqs: [
        { q: 'Сколько стоит?', a: 'Первичная — 7 000 RSD, контрольная — 5 000 RSD.' },
        { q: 'Какие специалисты?', a: 'Терапевт, кардиолог, гастроэнтеролог, дерматолог, ортопед, ЛОР, невропсихиатр, психолог, уролог, эндокринолог.' },
        { q: 'Направление?', a: 'Не требуется.' },
        { q: 'Подготовка?', a: 'Не нужна.' },
        { q: 'Длительность?', a: '20–30 минут.' },
        { q: 'Приём специалиста цена в Новом Белграде?', a: 'Первичная — 7 000 RSD, контрольная — 5 000. Все специалисты — Bulevar umetnosti 15.' },
      ],
    },
  },
];

export const findDiagService = (slug: string) =>
  DIAGNOSTIC_SERVICES.find((s) => s.slug === slug);
